import React, { useState } from 'react';
import { PaymentSource } from './types';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { Settings, LogOut, LayoutDashboard, Target, Users, Calendar, ArrowRightLeft, ArrowUpRight, ArrowDownRight, Edit2, Plus, PieChart, TrendingUp, History, User, CheckCircle2, AlertCircle, ShoppingCart, Clock, Car, Home, Utensils, Zap, Shield, Trash2, X, CreditCard, Bell, ChevronLeft, ChevronRight, Smartphone, HeartPulse } from 'lucide-react';
import { useAuth } from './contexts/AuthContext';
import { useSupabaseData } from './hooks/useSupabaseData';
import { createExpense, createMonth, createAdvance, createRepayment, updateAdvance, deleteAdvance, createBudgets, getBudgets, updateBudget, deleteBudget, updateExpense, deleteExpense, deleteIncomesForMonth, updateSettings, getMonth, getActiveMonthFromDB, setActiveMonthInDB, getAnalyticsData } from './services/supabaseService';
import { requestNotificationPermissions, scheduleTestNotification, scheduleBillReminders, triggerInstantNotification } from './services/notificationService';

// --- MOCK DATA ---
const MOCK_DATA = {
  resteAVivre: 845.50,
  surLeCompte: 1250.00,
  upcomingBills: [
    { id: 1, title: 'Électricité (EDF)', amount: 120.00, daysLeft: 2, icon: Zap },
    { id: 2, title: 'Abonnement Internet', amount: 39.99, daysLeft: 4, icon: Smartphone },
  ],
  recentTransactions: [
    { id: 1, title: 'Loyer', amount: -800.00, date: '5 Mai', category: 'Logement', icon: ShoppingCart, budgetId: 1 },
    { id: 2, title: 'Virement Océane', amount: 150.00, date: 'Hier', category: 'Avance', icon: ArrowRightLeft },
    { id: 3, title: 'Électricité partiel', amount: -60.00, date: '12 Mai', category: 'Logement', icon: Zap, budgetId: 2 },
    { id: 4, title: 'Essence Total', amount: -70.00, date: '16 Mai', category: 'Transport', icon: Car },
    { id: 5, title: 'Pharmacie', amount: -12.50, date: '15 Mai', category: 'Santé', icon: HeartPulse },
    { id: 6, title: 'Box Internet', amount: -39.99, date: '14 Mai', category: 'Abonnements', icon: Smartphone, budgetId: 3 },
  ],
  budgetsCommuns: [
    { id: 1, name: 'Vie Quotidienne', spent: 650, total: 800 },
    { id: 2, name: 'Loisirs & Sorties', spent: 180, total: 200 },
    { id: 3, name: 'Enfant', spent: 45, total: 150 },
    { id: 4, name: 'Voiture', spent: 140, total: 150 },
  ],
  budgetsPersos: [
    { name: 'Emmanuel', spent: 150, total: 300 },
    { name: 'Océane', spent: 280, total: 300 },
  ],
  advances: {
    emmanuelOwes: 150,
    oceaneOwes: 0,
    history: [
      { id: 1, payer: 'Océane', for: 'Emmanuel', amount: 150, reason: 'Réparation vélo', date: 'Hier', settled: false },
      { id: 2, payer: 'Emmanuel', for: 'Commun', amount: 50, reason: 'Avance courses', date: '10 Mai', settled: true },
      { id: 3, payer: 'Océane', for: 'Commun', amount: 20, reason: 'Boulangerie', date: '8 Mai', settled: true },
    ]
  }
};

// --- ANIMATIONS ---
const pageVariants: any = {
  initial: { opacity: 0, y: 20, scale: 0.98 },
  in: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4, ease: "easeOut", staggerChildren: 0.1 } },
  out: { opacity: 0, y: -20, scale: 0.98, transition: { duration: 0.2, ease: "easeIn" } }
};

const itemVariants: any = {
  initial: { opacity: 0, y: 15 },
  in: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 400, damping: 30 } }
};

export const SupabasePrototype = () => {
  const { currentUser, logout } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Real data from Supabase
  const { loading: dataLoading, activeMonth, expenses: realExpenses, budgets: realBudgets, advances: realAdvances, settings, refreshData } = useSupabaseData(currentDate);

  const [activeTab, setActiveTab] = useState('home');
  const [budgetTab, setBudgetTab] = useState('communs'); // 'communs' | 'persos'
  const [budgetCommunsView, setBudgetCommunsView] = useState<'postes' | 'categories'>('postes'); // 'postes' | 'categories'
  const [selectedPerson, setSelectedPerson] = useState<string | null>(null);
  const [isAddExpenseOpen, setIsAddExpenseOpen] = useState(false);
  const [isEditExpenseOpen, setIsEditExpenseOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<any>(null);
  const [isAddAdvanceOpen, setIsAddAdvanceOpen] = useState(false);
  const [editingAdvance, setEditingAdvance] = useState<any>(null);
  const [repayingAdvance, setRepayingAdvance] = useState<any>(null);
  const [selectedAdvance, setSelectedAdvance] = useState<any | null>(null);
  const [advancesHistory, setAdvancesHistory] = useState<any[]>(MOCK_DATA.advances.history.map(a => ({...a, repayments: []})));
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [activeSettingsTab, setActiveSettingsTab] = useState<'trame' | 'sources' | 'categories' | 'compte'>('trame');
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  
  const [isAddBudgetOpen, setIsAddBudgetOpen] = useState(false);
  const [isAddMonthlyPosteOpen, setIsAddMonthlyPosteOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState<any>(null);
  const [editingMonthlyPoste, setEditingMonthlyPoste] = useState<any>(null);
  const [newBudgetForm, setNewBudgetForm] = useState({
    name: '', total: '', type: 'commun', owner: 'Emmanuel'
  });
  const [newMonthlyPosteForm, setNewMonthlyPosteForm] = useState({
    title: '', amount: '', due_date: '', category: ''
  });
  const [expenseIsPaid, setExpenseIsPaid] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState(() => {
    const saved = localStorage.getItem('notification_prefs');
    return saved ? JSON.parse(saved) : {
      billReminders: true,
      budgetAlerts: true,
      advancesAlerts: true,
      monthlySummaryAlerts: true
    };
  });

  React.useEffect(() => {
    localStorage.setItem('notification_prefs', JSON.stringify(notificationPrefs));
  }, [notificationPrefs]);


  const [expenseCategories, setExpenseCategories] = useState([
    { id: '1', name: 'Alimentation' },
    { id: '2', name: 'Logement' },
    { id: '3', name: 'Transport & Voiture' },
    { id: '4', name: 'Loisirs & Sorties' },
    { id: '5', name: 'Santé' },
    { id: '6', name: 'Shopping' },
    { id: '7', name: 'Autre' }
  ]);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [salaireEmmanuel, setSalaireEmmanuel] = useState('2500');
  const [salaireOceane, setSalaireOceane] = useState('2500');
  const [aides, setAides] = useState('350');
  const [simSalaireEmmanuel, setSimSalaireEmmanuel] = useState(() => localStorage.getItem('sim_salaire_emmanuel') || '2500');
  const [simSalaireOceane, setSimSalaireOceane] = useState(() => localStorage.getItem('sim_salaire_oceane') || '2500');
  const [simAides, setSimAides] = useState(() => localStorage.getItem('sim_aides') || '350');
  const [showSimSaved, setShowSimSaved] = useState(false);

  const [transactions, setTransactions] = useState(MOCK_DATA.recentTransactions);
  const [expenseIsBudgetized, setExpenseIsBudgetized] = useState(false);
  const [expenseBudgetId, setExpenseBudgetId] = useState<string | ''>('');
  const EXPENSE_CATEGORIES = [
    'Alimentation', 'Logement', 'Transport', 'Entretien voiture', 
    'Santé', 'Loisirs', 'Enfant', 'Avance', 'Autre'
  ];

  const [defaultExpenses, setDefaultExpenses] = useState<any[]>([]);

  // Charge le mois actif configuré en base au démarrage
  React.useEffect(() => {
    const fetchActiveMonthOnStartup = async () => {
      try {
        const active = await getActiveMonthFromDB();
        if (active) {
          const [year, month] = active.month_key.split('-');
          setCurrentDate(new Date(Number(year), Number(month) - 1, 1));
        }
      } catch (err) {
        console.error("Erreur lors du chargement du mois actif au démarrage:", err);
      }
    };
    fetchActiveMonthOnStartup();
  }, []);
  // Synchronise automatiquement les rappels de prélèvements locaux sur l'iPhone
  React.useEffect(() => {
    if (realExpenses && realExpenses.length > 0) {
      scheduleBillReminders(realExpenses, notificationPrefs.billReminders);
    }
  }, [realExpenses, notificationPrefs.billReminders]);

  React.useEffect(() => {
    if (settings?.default_expenses_template) {
      setDefaultExpenses(settings.default_expenses_template);
    } else {
      setDefaultExpenses([]);
    }
    if (settings?.default_salaries && Array.isArray(settings.default_salaries)) {
      const salEmmanuel = settings.default_salaries.find((s: any) => s.name === 'emmanuel')?.amount;
      const salOceane = settings.default_salaries.find((s: any) => s.name === 'oceane')?.amount;
      const aidesVal = settings.default_salaries.find((s: any) => s.name === 'aides')?.amount;
      if (salEmmanuel !== undefined) setSimSalaireEmmanuel(salEmmanuel.toString());
      if (salOceane !== undefined) setSimSalaireOceane(salOceane.toString());
      if (aidesVal !== undefined) setSimAides(aidesVal.toString());
    }
    if (settings?.payment_sources && Array.isArray(settings.payment_sources)) {
      setPaymentSources(settings.payment_sources);
    }
  }, [settings]);

  console.log('DEBUG: realBudgets is:', realBudgets, 'activeMonth is:', activeMonth);

  React.useEffect(() => {
    const incomes = realExpenses.filter(e => e.category === 'revenu');
    const emmanuelIncome = incomes.find(e => e.title === 'Salaire Emmanuel');
    const oceaneIncome = incomes.find(e => e.title === 'Salaire Océane');
    const aidesIncome = incomes.find(e => e.title === 'Aides & Autres revenus' || e.title === 'Aides');
    
    if (emmanuelIncome) {
      setSalaireEmmanuel(emmanuelIncome.amount.toString());
    } else {
      setSalaireEmmanuel('2500');
    }
    
    if (oceaneIncome) {
      setSalaireOceane(oceaneIncome.amount.toString());
    } else {
      setSalaireOceane('2500');
    }
    
    if (aidesIncome) {
      setAides(aidesIncome.amount.toString());
    } else {
      setAides('350');
    }
  }, [realExpenses]);

  // Global calculations for Reste à Vivre and Personal Envelopes
  const currentIncomes = realExpenses.filter(e => e.category === 'revenu');
  const currentTotalRevenus = currentIncomes.length > 0 
    ? currentIncomes.reduce((sum, e) => sum + e.amount, 0) 
    : (activeMonth ? 5350 : ((Number(simSalaireEmmanuel) || 0) + (Number(simSalaireOceane) || 0) + (Number(simAides) || 0)));

  const currentMonthlyBills = activeMonth 
    ? realExpenses.filter(e => e.category === 'mensuel' || e.category?.startsWith('mensuel'))
    : defaultExpenses;
  const currentTotalMensuel = currentMonthlyBills.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);

  const calculatedResteAVivre = Math.max(0, currentTotalRevenus - currentTotalMensuel);
  const calculatedResteChacun = calculatedResteAVivre / 2;

  const selectedPersonBudget = selectedPerson ? (() => {
    const bPersos = realBudgets.filter(b => b.owner === selectedPerson);
    const firstBudget = bPersos[0];
    const total = calculatedResteChacun;

    const personalExpenses = realExpenses.filter(e => 
      e.category !== 'mensuel' && !e.category?.startsWith('mensuel') && e.category !== 'revenu' &&
      (
        (e.budget_id && bPersos.some(b => b.id === e.budget_id)) ||
        (firstBudget && e.budget_id === firstBudget.id) ||
        (e.category && e.category.toLowerCase() === selectedPerson.toLowerCase()) ||
        (e.title && e.title.toLowerCase().includes(selectedPerson.toLowerCase()))
      )
    );

    const spent = personalExpenses.filter(e => e.is_paid).reduce((sum, e) => sum + e.amount, 0);

    return {
      id: firstBudget?.id,
      name: selectedPerson,
      total,
      spent,
      remaining: total - spent,
      expenses: personalExpenses
    };
  })() : null;

  const [isInitializingBudgets, setIsInitializingBudgets] = useState(false);

  React.useEffect(() => {
    const autoInitBudgets = async () => {
      if (!activeMonth || isInitializingBudgets || realExpenses.length === 0) return;
      
      // Find budgetized monthly expenses of the active month
      const budgetizedMonthly = realExpenses.filter(e => 
        (e.category === 'mensuel' || e.category?.startsWith('mensuel')) && 
        e.is_budgetized !== false
      );
      
      const budgetsToCreate: any[] = [];
      const expensesToUpdate: { expenseId: string, budgetName: string }[] = [];
      
      for (const e of budgetizedMonthly) {
        // Check if there is an associated budget in realBudgets
        const associatedBudget = realBudgets.find(b => 
          b.id === e.budget_id || b.name.toLowerCase() === e.title.toLowerCase()
        );
        
        if (!associatedBudget) {
          budgetsToCreate.push({
            month_id: activeMonth.id,
            name: e.title,
            total: Number(e.amount) || 0,
            type: (e.title.toLowerCase().includes('emmanuel') || e.title.toLowerCase().includes('océane') || e.title.toLowerCase().includes('océanne') ? 'perso' : 'commun') as 'commun' | 'perso',
            owner: e.title.toLowerCase().includes('emmanuel') ? 'Emmanuel' : (e.title.toLowerCase().includes('océane') || e.title.toLowerCase().includes('océanne') ? 'Océane' : null)
          });
          expensesToUpdate.push({ expenseId: e.id, budgetName: e.title });
        } else if (!e.budget_id || e.budget_id !== associatedBudget.id) {
          // If the budget exists but the expense is not linked, we link it!
          expensesToUpdate.push({ expenseId: e.id, budgetName: e.title });
        }
      }
      
      for (const name of ['Emmanuel', 'Océane']) {
        const hasPersoBudget = realBudgets.some(b => b.owner === name);
        if (!hasPersoBudget) {
          budgetsToCreate.push({
            month_id: activeMonth.id,
            name: `Dépenses ${name}`,
            total: calculatedResteChacun,
            type: 'perso' as const,
            owner: name
          });
        }
      }

      // Check for duplicates or orphaned budgets
      const seenNames = new Set<string>();
      const budgetsToDelete: string[] = [];
      for (const b of realBudgets) {
        const lowerName = b.name.toLowerCase();
        const matchesMonthly = budgetizedMonthly.some(e => e.title.toLowerCase() === lowerName || e.budget_id === b.id);
        const isPersonalBudget = b.type === 'perso';
        
        if (seenNames.has(lowerName)) {
          budgetsToDelete.push(b.id);
        } else if (!matchesMonthly && !isPersonalBudget) {
          budgetsToDelete.push(b.id);
        } else {
          seenNames.add(lowerName);
        }
      }

      if (budgetsToCreate.length > 0 || expensesToUpdate.length > 0 || budgetsToDelete.length > 0) {
        setIsInitializingBudgets(true);
        try {
          // 1. Delete duplicates or orphaned budgets
          if (budgetsToDelete.length > 0) {
            for (const id of budgetsToDelete) {
              await deleteBudget(id);
            }
          }

          // 2. Create missing budgets
          if (budgetsToCreate.length > 0) {
            await createBudgets(budgetsToCreate);
          }
          
          // 3. Fetch the newly created budgets to get their IDs
          const newBudgets = await getBudgets(activeMonth.id);
          
          // 4. Update monthly expenses with correct budget_id
          for (const item of expensesToUpdate) {
            const budgetRow = newBudgets?.find((b: any) => b.name.toLowerCase() === item.budgetName.toLowerCase());
            if (budgetRow) {
              await updateExpense(item.expenseId, { budget_id: budgetRow.id });
            }
          }
          
          refreshData();
        } catch (err) {
          console.error("Auto-linking budgets failed:", err);
        } finally {
          setIsInitializingBudgets(false);
        }
      }
    };
    autoInitBudgets();
  }, [activeMonth, realBudgets, realExpenses, isInitializingBudgets]);

  const [isDefaultExpenseModalOpen, setIsDefaultExpenseModalOpen] = useState(false);
  const [editingDefaultExpense, setEditingDefaultExpense] = useState<any>(null);

  // New Expense Form State
  const [newExpenseForm, setNewExpenseForm] = useState({
    title: '', amount: '', date: '', category: ''
  });

  const [toast, setToast] = useState<{message: string, action?: {label: string, onClick: () => void}} | null>(null);

  const showToast = (message: string, action?: {label: string, onClick: () => void}) => {
    setToast({ message, action });
    setTimeout(() => setToast(null), 4000);
  };

  // New Advance Form State
  const [newAdvanceForm, setNewAdvanceForm] = useState({
    payer: 'Emmanuel', forWhom: 'Commun', reason: '', amount: ''
  });

  const [repaymentAmount, setRepaymentAmount] = useState('');

  const handleRepayAdvance = async () => {
    if (!repayingAdvance) return;
    try {
      const amount = Number(repaymentAmount);
      if (amount <= 0) return;
      
      await createRepayment({
        advance_id: repayingAdvance.id,
        amount: amount
      });
      
      const totalRepaid = (repayingAdvance.repayments?.reduce((s:number,r:any)=>s+r.amount,0) || 0) + amount;
      if (totalRepaid >= repayingAdvance.amount) {
        await updateAdvance(repayingAdvance.id, { settled: true });
      }
      
      setRepayingAdvance(null);
      refreshData();
      showToast('Remboursement ajouté avec succès !', { label: 'Voir', onClick: () => setActiveTab('avances') });
    } catch (err) {
      console.error(err);
      alert("Erreur lors du remboursement.");
    }
  };

  const handleSettleDebt = async (slide: any) => {
    if (slide.amount <= 0) {
      alert("Il n'y a pas de dette à régler.");
      return;
    }

    if (!window.confirm(`Voulez-vous vraiment régler ${slide.isGlobal ? "l'ardoise globale" : "cette dette"} de ${slide.amount.toFixed(2)} € ?`)) return;

    try {
      const pending = realAdvances.filter(a => !a.settled);
      if (slide.isGlobal) {
        for (const adv of pending) {
          await updateAdvance(adv.id, { settled: true });
        }
        showToast("L'ardoise globale a été réglée avec succès !");
      } else {
        const [debtor, payer] = slide.title.split(' doit à ');
        const targetAdvances = pending.filter(a => a.payer === payer && a.for_whom === debtor);
        for (const adv of targetAdvances) {
          await updateAdvance(adv.id, { settled: true });
        }
        showToast(`La dette de ${debtor} envers ${payer} a été réglée !`);
      }
      refreshData();
    } catch (err) {
      console.error(err);
      alert("Erreur lors du règlement de la dette.");
    }
  };

  const getOrCreateMonthId = async () => {
    if (activeMonth?.id) return activeMonth.id;
    
    const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    try {
      const newMonth = await createMonth({
        month_key: monthKey,
        reste_a_vivre: 0,
        sur_le_compte: 0,
        is_active: false
      });
      
      if (newMonth) {
        const totalSimRevenus = (Number(simSalaireEmmanuel) || 0) + (Number(simSalaireOceane) || 0) + (Number(simAides) || 0);
        const resteParPersonne = (totalSimRevenus - defaultExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0)) / 2;
        const persoAmount = resteParPersonne > 0 ? resteParPersonne : 0;

        // 1. Create budgets for template items that are budgetized
        const budgetsToCreate: any[] = defaultExpenses
          .filter(t => t.isBudgetized !== false)
          .map(t => {
            const isEmmanuel = t.title.toLowerCase().includes('emmanuel');
            const isOceane = t.title.toLowerCase().includes('océane') || t.title.toLowerCase().includes('océanne');
            const isPerso = isEmmanuel || isOceane;
            return {
              month_id: newMonth.id,
              name: t.title,
              total: isPerso ? persoAmount : (Number(t.amount) || 0),
              type: (isPerso ? 'perso' : 'commun') as 'commun' | 'perso',
              owner: isEmmanuel ? 'Emmanuel' : (isOceane ? 'Océane' : null)
            };
          });

        for (const name of ['Emmanuel', 'Océane']) {
          if (!budgetsToCreate.some(b => b.owner === name)) {
            budgetsToCreate.push({
              month_id: newMonth.id,
              name: `Dépenses ${name}`,
              total: persoAmount,
              type: 'perso' as const,
              owner: name
            });
          }
        }
          
        if (budgetsToCreate.length > 0) {
          await createBudgets(budgetsToCreate);
        }
        
        const createdBudgets = await getBudgets(newMonth.id);

        // 2. Copy template items into the database as real expenses for this month
        for (const t of defaultExpenses) {
          const day = String(t.dueDate || '01').padStart(2, '0');
          const fullDate = `${monthKey}-${day}`;
          const linkedBudget = createdBudgets?.find(b => b.name.toLowerCase() === t.title.toLowerCase());
          const isEmmanuel = t.title.toLowerCase().includes('emmanuel');
          const isOceane = t.title.toLowerCase().includes('océane') || t.title.toLowerCase().includes('océanne');
          const isPerso = isEmmanuel || isOceane;

          await createExpense({
            month_id: newMonth.id,
            title: t.title,
            amount: isPerso ? persoAmount : (Number(t.amount) || 0),
            category: t.category ? `mensuel_${t.category}` : 'mensuel',
            due_date: fullDate,
            is_paid: false,
            is_budgetized: t.isBudgetized ?? true,
            budget_id: linkedBudget ? linkedBudget.id : null
          });
        }
        refreshData();
        return newMonth.id;
      }
    } catch (err) {
      console.error("Error creating month and templates:", err);
    }
    return null;
  };

  const handleAddBudget = async () => {
    const monthId = await getOrCreateMonthId();
    if (!monthId) return;
    try {
      if (editingBudget) {
        await updateBudget(editingBudget.id, {
          name: newBudgetForm.name,
          total: Number(newBudgetForm.total),
          type: newBudgetForm.type as any,
          owner: newBudgetForm.type === 'perso' ? newBudgetForm.owner : null
        });
        showToast('Budget modifié avec succès !');
      } else {
        await createBudgets([{
          month_id: monthId,
          name: newBudgetForm.name,
          total: Number(newBudgetForm.total),
          type: newBudgetForm.type as any,
          owner: newBudgetForm.type === 'perso' ? newBudgetForm.owner : null
        }]);
        showToast('Budget ajouté avec succès !');
      }
      setIsAddBudgetOpen(false);
      setEditingBudget(null);
      setNewBudgetForm({ name: '', total: '', type: 'commun', owner: 'Emmanuel' });
      refreshData();
    } catch (err) {
      console.error(err);
      alert("Erreur lors de l'enregistrement du budget.");
    }
  };

  const handleAddMonthlyPoste = async () => {
    const monthId = await getOrCreateMonthId();
    if (!monthId) return;
    try {
      let fullDate = newMonthlyPosteForm.due_date || '01';
      if (!fullDate.includes('-')) {
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(fullDate).padStart(2, '0');
        fullDate = `${year}-${month}-${day}`;
      }

      const fullCategory = newMonthlyPosteForm.category ? `mensuel_${newMonthlyPosteForm.category}` : 'mensuel';
      if (editingMonthlyPoste) {
        await updateExpense(editingMonthlyPoste.id, {
          title: newMonthlyPosteForm.title,
          amount: Number(newMonthlyPosteForm.amount),
          due_date: fullDate,
          category: fullCategory,
          is_budgetized: expenseIsBudgetized,
          is_paid: expenseIsPaid
        });
        showToast('Poste mensuel modifié avec succès !');
      } else {
        await createExpense({
          month_id: monthId,
          title: newMonthlyPosteForm.title,
          amount: Number(newMonthlyPosteForm.amount),
          category: fullCategory,
          due_date: fullDate,
          is_paid: expenseIsPaid,
          is_budgetized: expenseIsBudgetized,
          budget_id: null
        });
        showToast('Poste mensuel ajouté avec succès !');
      }
      setIsAddMonthlyPosteOpen(false);
      setEditingMonthlyPoste(null);
      setNewMonthlyPosteForm({ title: '', amount: '', due_date: '', category: '' });
      setExpenseIsPaid(false);
      refreshData();
    } catch (err) {
      console.error(err);
      alert("Erreur lors de l'enregistrement du poste.");
    }
  };

  const handleAddAdvance = async () => {
    try {
      if (editingAdvance) {
        await updateAdvance(editingAdvance.id, {
          payer: newAdvanceForm.payer,
          for_whom: newAdvanceForm.forWhom,
          amount: Number(newAdvanceForm.amount) || 0,
          reason: newAdvanceForm.reason
        });
        showToast('Avance modifiée avec succès !', { label: 'Voir', onClick: () => setActiveTab('avances') });
      } else {
        await createAdvance({
          payer: newAdvanceForm.payer,
          for_whom: newAdvanceForm.forWhom,
          amount: Number(newAdvanceForm.amount) || 0,
          reason: newAdvanceForm.reason,
          settled: false
        });
        showToast('Avance ajoutée avec succès !', { label: 'Voir', onClick: () => setActiveTab('avances') });
      }
      setIsAddAdvanceOpen(false);
      setEditingAdvance(null);
      setNewAdvanceForm({ payer: 'Emmanuel', forWhom: 'Commun', reason: '', amount: '' });
      refreshData();
    } catch (err) {
      console.error(err);
      alert("Erreur lors de l'enregistrement de l'avance.");
    }
  };
  const handleDeleteAdvance = async (id: string) => {
    if (!window.confirm("Voulez-vous vraiment supprimer cette avance ?")) return;
    try {
      await deleteAdvance(id);
      refreshData();
      showToast("Avance supprimée avec succès !");
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la suppression de l'avance.");
    }
  };

  const handleAddExpense = async () => {
    try {
      if (!newExpenseForm.title || !newExpenseForm.amount) {
        alert("Veuillez renseigner au moins un titre et un montant.");
        return;
      }

      const isInitialMonth = !activeMonth;
      const monthId = await getOrCreateMonthId();
      if (!monthId) return;

      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(newExpenseForm.date || '01').padStart(2, '0');
      const fullDate = `${year}-${month}-${day}`;

      let finalBudgetId: string | null = expenseIsBudgetized && expenseBudgetId ? String(expenseBudgetId) : null;
      if (finalBudgetId && isInitialMonth) {
        // If the month was just initialized, find the newly created budget's UUID matching the selected template title
        const newBudgets = await getBudgets(monthId);
        const matched = newBudgets.find(b => b.name.toLowerCase() === finalBudgetId?.toLowerCase());
        if (matched) {
          finalBudgetId = matched.id;
        }
      }

      // If adding from personal detail modal (selectedPerson is set)
      if (selectedPerson) {
        let currentBudgets = await getBudgets(monthId);
        let personBudget = currentBudgets.find(b => b.owner === selectedPerson);
        if (!personBudget) {
          // Create the personal budget in Supabase for this person
          await createBudgets([{
            month_id: monthId,
            name: `Dépenses ${selectedPerson}`,
            total: calculatedResteChacun,
            type: 'perso',
            owner: selectedPerson
          }]);
          const afterBudgets = await getBudgets(monthId);
          personBudget = afterBudgets.find(b => b.owner === selectedPerson);
        }
        if (personBudget) {
          finalBudgetId = personBudget.id;
        }
      }

      await createExpense({
        month_id: monthId,
        budget_id: finalBudgetId,
        title: newExpenseForm.title,
        amount: Number(newExpenseForm.amount) || 0,
        category: newExpenseForm.category || (selectedPerson ? selectedPerson : 'Autre'),
        due_date: fullDate,
        is_paid: true,
        is_budgetized: !!finalBudgetId || expenseIsBudgetized
      });

      setIsAddExpenseOpen(false);
      setNewExpenseForm({ title: '', amount: '', date: '', category: '' });
      setExpenseBudgetId('');
      setExpenseIsBudgetized(false);
      await refreshData();
      showToast('Dépense ajoutée avec succès !');
      triggerInstantNotification(
        "Dépense ajoutée 💸",
        `La dépense "${newExpenseForm.title}" de ${(Number(newExpenseForm.amount) || 0).toFixed(2)} € a été ajoutée.`
      );
    } catch (error: any) {
      console.error(error);
      alert("Erreur lors de l'ajout : " + (error?.message || JSON.stringify(error)));
    }
  };

  const handleDeleteExpense = async (id: string) => {
    if (!window.confirm("Voulez-vous vraiment supprimer cette dépense ?")) return;
    try {
      await deleteExpense(id);
      showToast("Dépense supprimée avec succès !");
      refreshData();
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la suppression de la dépense.");
    }
  };

  const handleUpdateExpense = async () => {
    if (!editingExpense) return;
    try {
      await updateExpense(editingExpense.id, {
        title: editingExpense.title,
        amount: Number(editingExpense.amount) || 0,
        due_date: editingExpense.due_date,
        category: editingExpense.category,
        budget_id: expenseIsBudgetized && expenseBudgetId ? String(expenseBudgetId) : (editingExpense.budget_id || null),
        is_budgetized: expenseIsBudgetized || !!editingExpense.budget_id
      });
      setIsEditExpenseOpen(false);
      setEditingExpense(null);
      await refreshData();
      showToast("Dépense modifiée avec succès !");
    } catch (err: any) {
      console.error(err);
      alert("Erreur lors de la modification de la dépense : " + (err?.message || JSON.stringify(err)));
    }
  };
  
  const [postesFilter, setPostesFilter] = useState<'Toutes' | 'À venir' | 'Passées' | 'Budgets'>('Toutes');

  const saveDefaultExpensesToSupabase = async (updatedList: any[]) => {
    if (!settings?.id) return;
    try {
      await updateSettings(settings.id, {
        default_expenses_template: updatedList
      });
      refreshData();
    } catch (err) {
      console.error("Erreur lors de la sauvegarde des paramètres:", err);
      alert("Erreur lors de l'enregistrement de la trame par défaut.");
    }
  };
  
  const saveSimSalariesToSupabase = async (emmanuel: string, oceane: string, aidesVal: string) => {
    if (!settings?.id) return;
    try {
      await updateSettings(settings.id, {
        default_salaries: [
          { name: 'emmanuel', amount: Number(emmanuel) || 0 },
          { name: 'oceane', amount: Number(oceane) || 0 },
          { name: 'aides', amount: Number(aidesVal) || 0 }
        ]
      });
      refreshData();
      setShowSimSaved(true);
      setTimeout(() => setShowSimSaved(false), 2000);
    } catch (err) {
      console.error("Erreur lors de la sauvegarde des salaires simulés:", err);
    }
  };

  const handleSaveDefaultExpense = async () => {
    if (editingDefaultExpense) {
      const amount = Number(editingDefaultExpense.amount) || 0;
      const dueDate = editingDefaultExpense.dueDate || '';
      const isPaid = editingDefaultExpense.isPaid || false;
      const isBudgetized = editingDefaultExpense.isBudgetized ?? true;
      let updatedList = [];
      if (editingDefaultExpense.id === 0) {
        updatedList = [...defaultExpenses, { ...editingDefaultExpense, id: Date.now(), amount, dueDate, isPaid, isBudgetized }];
      } else {
        updatedList = defaultExpenses.map(e => e.id === editingDefaultExpense.id ? { ...editingDefaultExpense, amount, dueDate, isPaid, isBudgetized } : e);
      }
      setDefaultExpenses(updatedList);
      await saveDefaultExpensesToSupabase(updatedList);
      showToast("Poste de la trame par défaut enregistré !");
    }
    setIsDefaultExpenseModalOpen(false);
  };

  // New state for payment sources
  const [paymentSources, setPaymentSources] = useState<PaymentSource[]>(() => {
    const saved = localStorage.getItem('payment_sources');
    return saved ? JSON.parse(saved) : [
      { id: '1', name: 'Hello Bank', owner: 'commun' },
      { id: '2', name: 'Boursorama', owner: 'commun' },
      { id: '3', name: 'Compte d\'Emmanuel', owner: 'emmanuel' },
      { id: '4', name: 'Compte d\'Océane', owner: 'oceanne' },
      { id: '5', name: 'Liquide Emmanuel', owner: 'emmanuel' },
      { id: '6', name: 'Liquide Océane', owner: 'oceanne' },
    ];
  });
  
  React.useEffect(() => {
    localStorage.setItem('payment_sources', JSON.stringify(paymentSources));
  }, [paymentSources]);

  const [isPaymentSourceModalOpen, setIsPaymentSourceModalOpen] = useState(false);
  const [editingPaymentSource, setEditingPaymentSource] = useState<PaymentSource | null>(null);

  const savePaymentSourcesToSupabase = async (sources: PaymentSource[]) => {
    if (!settings?.id) return;
    try {
      await updateSettings(settings.id, {
        payment_sources: sources
      });
      refreshData();
    } catch (err) {
      console.error("Erreur lors de la sauvegarde des sources de paiement:", err);
    }
  };

  const handleSavePaymentSource = async () => {
    if (editingPaymentSource) {
      let updatedSources = [];
      if (editingPaymentSource.id === '0') {
        updatedSources = [...paymentSources, { ...editingPaymentSource, id: Date.now().toString() }];
        setPaymentSources(updatedSources);
        showToast("Source de paiement ajoutée avec succès !");
      } else {
        updatedSources = paymentSources.map(s => s.id === editingPaymentSource.id ? editingPaymentSource : s);
        setPaymentSources(updatedSources);
        showToast("Source de paiement modifiée avec succès !");
      }
      await savePaymentSourcesToSupabase(updatedSources);
    }
    setIsPaymentSourceModalOpen(false);
  };

  const handleSaveResources = async () => {
    try {
      const monthId = await getOrCreateMonthId();
      if (!monthId) return;

      // Delete existing incomes
      await deleteIncomesForMonth(monthId);

      // Create new incomes
      await createExpense({
        month_id: monthId,
        title: 'Salaire Emmanuel',
        amount: Number(salaireEmmanuel) || 0,
        category: 'revenu',
        due_date: `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-01`,
        is_paid: true,
        is_budgetized: false,
        budget_id: null
      });

      await createExpense({
        month_id: monthId,
        title: 'Salaire Océane',
        amount: Number(salaireOceane) || 0,
        category: 'revenu',
        due_date: `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-01`,
        is_paid: true,
        is_budgetized: false,
        budget_id: null
      });

      await createExpense({
        month_id: monthId,
        title: 'Aides & Autres revenus',
        amount: Number(aides) || 0,
        category: 'revenu',
        due_date: `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-01`,
        is_paid: true,
        is_budgetized: false,
        budget_id: null
      });

      setIsResourcesOpen(false);
      refreshData();
      showToast("Ressources mensuelles enregistrées !");
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la sauvegarde.");
    }
  };

  const HomeTab = () => {
    const incomes = realExpenses.filter(e => e.category === 'revenu');
    const totalRevenus = incomes.length > 0 
      ? incomes.reduce((sum, e) => sum + e.amount, 0) 
      : 5350;

    const monthlyBills = realExpenses.filter(e => e.category === 'mensuel' || e.category?.startsWith('mensuel'));
    const totalMensuel = monthlyBills.reduce((sum, e) => sum + e.amount, 0);

    const dailyExpenses = realExpenses.filter(e => e.category !== 'mensuel' && !e.category?.startsWith('mensuel') && e.category !== 'revenu' && !e.is_budgetized);
    const totalDaily = dailyExpenses.reduce((sum, e) => sum + e.amount, 0);

    const resteAVivre = Math.max(0, totalRevenus - totalMensuel);

    const paidMonthly = monthlyBills.filter(e => e.is_paid).reduce((sum, e) => sum + e.amount, 0);
    const surLeCompte = monthlyBills.filter(e => !e.is_paid).reduce((sum, e) => sum + e.amount, 0);

    const upcomingBills = monthlyBills.filter(e => !e.is_paid).map(e => ({
      id: e.id,
      title: e.title,
      amount: Number(e.amount),
      dueDate: e.due_date ? (() => {
        let raw = e.due_date;
        if (raw.length > 10 && raw.includes('-')) {
          const parts = raw.split('-');
          if (parts.length > 3) {
            raw = `${parts[0]}-${parts[1]}-${parts[parts.length - 1]}`;
          }
        }
        const d = new Date(raw);
        return isNaN(d.getTime()) 
          ? raw 
          : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
      })() : '?',
      icon: ShoppingCart
    }));

    const pastTransactions = monthlyBills.filter(e => e.is_paid).map(e => ({
      id: e.id,
      title: e.title,
      amount: Number(e.amount),
      date: e.due_date ? (() => {
        let raw = e.due_date;
        if (raw.length > 10 && raw.includes('-')) {
          const parts = raw.split('-');
          if (parts.length > 3) {
            raw = `${parts[0]}-${parts[1]}-${parts[parts.length - 1]}`;
          }
        }
        const d = new Date(raw);
        return isNaN(d.getTime()) 
          ? raw 
          : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
      })() : '?',
      icon: ShoppingCart
    }));

    return (
      <motion.div key="home" variants={pageVariants} initial="initial" animate="in" exit="out" className="space-y-6">
        {/* Reste à Vivre */}
        <motion.div variants={itemVariants} className="relative group">
          <div className="absolute -inset-0.5 bg-[#ff154d]/20 rounded-2xl blur-lg opacity-70"></div>
          <div className="relative bg-[#171118] border border-[#ff154d] rounded-2xl p-6 shadow-[0_0_15px_rgba(255,21,77,0.2)] text-center">
            <button 
              onClick={() => setIsResourcesOpen(true)}
              className="absolute top-4 right-4 p-2 bg-[#211824] border border-[#3d2e40] rounded-full text-[#a89ea6] hover:text-white hover:border-[#ff154d] transition-colors shadow-sm"
            >
              <Edit2 className="w-3.5 h-3.5" />
            </button>
            <p className="text-[15px] text-[#a89ea6] font-medium mb-1">Reste à vivre (Global)</p>
            <h1 className="text-[48px] leading-none font-bold tracking-tight text-white mb-2" style={{ fontFamily: "'Lekton', monospace" }}>
              {resteAVivre.toFixed(2)} <span className="text-[32px]">€</span>
            </h1>
            <div className="flex flex-wrap justify-center gap-2 mt-3">
              <p className="text-[14px] text-[#ff154d] font-semibold bg-[#ff154d]/10 px-3 py-1.5 rounded-full border border-[#ff154d]/30">
                {(resteAVivre / 2).toFixed(2)} € chacun
              </p>
              <p className="text-[14px] text-[#a89ea6] font-medium bg-[#211824] px-3 py-1.5 rounded-full border border-[#3d2e40]">
                Prévoir compte : {surLeCompte.toFixed(2)} €
              </p>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div variants={itemVariants} className="grid grid-cols-2 gap-3">
          <button onClick={() => setIsAddExpenseOpen(true)} className="bg-[#ff154d] hover:bg-[#ff3b68] text-white text-[15px] font-bold py-3.5 rounded-xl transition-all flex items-center justify-center shadow-lg shadow-[#ff154d]/25 border border-[#ff154d]/50 active:scale-95">
            <Plus className="w-5 h-5 mr-2" /> Dépense
          </button>
          <button onClick={() => setIsAddAdvanceOpen(true)} className="bg-[#211824] hover:bg-[#2d2230] text-white text-[15px] font-bold py-3.5 rounded-xl transition-all flex items-center justify-center border border-[#3d2e40] active:scale-95">
            <ArrowRightLeft className="w-5 h-5 mr-2 text-[#a89ea6]" /> Avance
          </button>
        </motion.div>

        {/* Prélèvements */}
        <motion.div variants={itemVariants}>
          <div className="flex items-center mb-3 px-1">
            <AlertCircle className="w-4 h-4 text-[#ff154d] mr-2" />
            <h2 className="text-[16px] font-bold text-[#ff154d]">Prélèvements imminents</h2>
          </div>
          <div className="bg-[#171118] border border-[#2d2230] rounded-2xl overflow-hidden">
            {upcomingBills.length === 0 ? (
               <div className="p-4 text-center text-[#a89ea6] text-[13px]">Aucun prélèvement imminent.</div>
            ) : upcomingBills.map((bill, idx) => (
              <div key={bill.id} className={`flex items-center justify-between p-3.5 ${idx !== upcomingBills.length - 1 ? 'border-b border-[#2d2230]' : ''}`}>
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 bg-[#211824] border border-[#3d2e40] rounded-lg flex items-center justify-center">
                    <bill.icon className="w-4 h-4 text-[#a89ea6]" />
                  </div>
                  <div>
                    <p className="font-bold text-[15px] text-white">{bill.title}</p>
                    <p className="text-[13px] text-[#ff154d] font-medium">Le {bill.dueDate}</p>
                  </div>
                </div>
                <div className="text-right font-bold text-[16px] text-white" style={{ fontFamily: "'Lekton', monospace" }}>
                  -{bill.amount.toFixed(2)} €
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Prélèvements passés */}
        <motion.div variants={itemVariants}>
          <div className="flex items-center mb-3 px-1">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 mr-2" />
            <h2 className="text-[16px] font-bold text-white">Derniers prélèvements passés</h2>
          </div>
          <div className="bg-[#171118] border border-[#2d2230] rounded-2xl overflow-hidden opacity-80">
            {pastTransactions.length === 0 ? (
               <div className="p-4 text-center text-[#a89ea6] text-[13px]">Aucun prélèvement passé.</div>
            ) : pastTransactions.slice(0, 3).map((tx, idx, arr) => (
              <div key={tx.id} className={`flex items-center justify-between p-3.5 ${idx !== arr.length - 1 ? 'border-b border-[#2d2230]' : ''}`}>
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 bg-[#211824] border border-[#3d2e40] rounded-lg flex items-center justify-center">
                    <tx.icon className="w-4 h-4 text-[#a89ea6]" />
                  </div>
                  <div>
                    <p className="font-bold text-[15px] text-white line-through decoration-[#a89ea6]">{tx.title}</p>
                    <p className="text-[13px] text-[#8e848c] font-medium">Le {tx.date}</p>
                  </div>
                </div>
                <div className="text-right font-bold text-[16px] text-[#a89ea6]" style={{ fontFamily: "'Lekton', monospace" }}>
                  {tx.amount.toFixed(2)} €
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    );
  };

  const ExpensesTab = () => {
    const [expenseView, setExpenseView] = useState<'list' | 'categories'>('list');

    const filteredExpenses = realExpenses.filter(e => e.category !== 'mensuel' && !e.category?.startsWith('mensuel') && e.category !== 'revenu');

    const budgetsCommuns = realBudgets.filter(b => b.type === 'commun').map(b => {
      const spent = realExpenses.filter(e => e.budget_id === b.id && e.is_paid).reduce((sum, e) => sum + e.amount, 0);
      return { ...b, spent };
    });

    const formatDateToFrench = (dateStr: string) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    return (
      <motion.div variants={pageVariants} initial="initial" animate="in" exit="out" className="space-y-6">
        {/* Toggle View */}
        <motion.div variants={itemVariants} className="bg-[#171118] border border-[#2d2230] p-1.5 rounded-xl flex">
          <button 
            onClick={() => setExpenseView('list')}
            className={`flex-1 py-2 text-[14px] font-bold rounded-lg transition-colors flex items-center justify-center ${expenseView === 'list' ? 'bg-[#211824] text-white border border-[#3d2e40]' : 'text-[#a89ea6] hover:text-white'}`}
          >
            Liste des dépenses
          </button>
          <button 
            onClick={() => setExpenseView('categories')}
            className={`flex-1 py-2 text-[14px] font-bold rounded-lg transition-colors flex items-center justify-center ${expenseView === 'categories' ? 'bg-[#211824] text-white border border-[#3d2e40]' : 'text-[#a89ea6] hover:text-white'}`}
          >
            Répartition par catégorie
          </button>
        </motion.div>

        {expenseView === 'list' ? (
          <>
        {/* Actions */}
        <motion.div variants={itemVariants} className="flex flex-col space-y-3">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-[18px] font-bold text-white px-1">Liste des dépenses</h2>
            <button onClick={() => setIsAddExpenseOpen(true)} className="text-[13px] font-bold text-[#ff154d] bg-[#ff154d]/10 px-3 py-1.5 rounded-lg border border-[#ff154d]/30 flex items-center">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </button>
          </div>
        </motion.div>

        {/* Liste des Dépenses (Cards) */}
        <motion.div variants={itemVariants} className="space-y-3">
          {filteredExpenses.length === 0 ? (
            <div className="p-4 text-center text-[#a89ea6] text-[13px] bg-[#171118] rounded-xl border border-[#2d2230]">Aucune dépense trouvée.</div>
          ) : filteredExpenses.map((tx) => {
            const isPastDue = tx.is_paid;
            return (
              <div key={tx.id} className="bg-[#171118] rounded-xl border border-[#2d2230] p-3 shadow-sm relative overflow-hidden flex items-center justify-between">
                {/* Left: Icon + Text */}
                <div className="flex items-center space-x-3 overflow-hidden">
                  <div className="w-9 h-9 bg-[#211824] border border-[#3d2e40] rounded-lg flex items-center justify-center shrink-0">
                    <ShoppingCart className="w-4 h-4 text-[#a89ea6]" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-bold text-[14px] text-white truncate">{tx.title}</h4>
                    <div className="flex items-center space-x-2 mt-1 text-[11px] font-medium">
                      <span style={{ fontFamily: "'Lekton', monospace" }} className="text-[#a89ea6]">{formatDateToFrench(tx.due_date)}</span>
                      {tx.category && (
                        <>
                          <span>•</span>
                          <span className="text-[#ff154d] bg-[#ff154d]/10 px-2 py-0.5 rounded border border-[#ff154d]/20 text-[10px] font-bold">
                            {tx.category}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Right: Amount + Actions */}
                <div className="flex flex-col items-end shrink-0 ml-2">
                  <div className={`font-bold text-[15px] text-white`} style={{ fontFamily: "'Lekton', monospace" }}>
                    {Math.abs(tx.amount).toFixed(2)}€
                  </div>
                  <div className="flex space-x-2 mt-1.5">
                    <button 
                      onClick={() => {
                        setEditingExpense(tx);
                        setExpenseIsBudgetized(tx.is_budgetized || false);
                        setExpenseBudgetId(tx.budget_id || '');
                        setIsEditExpenseOpen(true);
                      }} 
                      className="text-[#a89ea6] hover:text-white transition-colors"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button 
                      onClick={() => handleDeleteExpense(tx.id)} 
                      className="text-[#a89ea6] hover:text-[#ff154d] transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </motion.div>
        </>
        ) : (
          <motion.div variants={itemVariants} className="bg-[#171118] border border-[#2d2230] rounded-2xl p-5 space-y-6">
            {budgetsCommuns.length === 0 ? (
              <div className="text-center text-[#a89ea6] text-[14px] py-8 flex flex-col items-center">
                <PieChart className="w-8 h-8 text-[#3d2e40] mb-3" />
                Aucune catégorie configurée pour ce mois.
              </div>
            ) : budgetsCommuns.map(budget => {
              const percent = budget.total > 0 ? Math.min((budget.spent / budget.total) * 100, 100) : 0;
              const isWarning = percent > 85;
              return (
                <div key={budget.id}>
                  <div className="flex justify-between items-end mb-2">
                    <p className="font-bold text-[15px] text-white">{budget.name}</p>
                    <p className="text-[14px] font-bold text-[#a89ea6]" style={{ fontFamily: "'Lekton', monospace" }}>
                      <span className={isWarning ? 'text-[#ff154d]' : 'text-white'}>{budget.spent.toFixed(2)}€</span> / {budget.total.toFixed(2)}€
                    </p>
                  </div>
                  <div className="h-2.5 w-full bg-[#211824] rounded-full overflow-hidden shadow-inner">
                    <motion.div 
                      initial={{ width: 0 }} animate={{ width: `${percent}%` }} transition={{ duration: 1, ease: "easeOut" }}
                      className={`h-full rounded-full ${isWarning ? 'bg-[#ff154d] shadow-[0_0_10px_rgba(255,21,77,0.5)]' : 'bg-purple-500'}`}
                    />
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}
        
      </motion.div>
    );
  };

  const BudgetsTab = () => {
    // Calcul des budgets communs à partir de Supabase
    const budgetsCommuns = realBudgets.filter(b => b.type === 'commun').map(b => {
      const spent = realExpenses.filter(e => e.budget_id === b.id && e.is_paid && e.category !== 'mensuel' && !e.category?.startsWith('mensuel')).reduce((sum, e) => sum + e.amount, 0);
      return { ...b, spent };
    });

    // Calcul des budgets personnels à partir de Supabase
    const budgetsPersos = ['Emmanuel', 'Océane'].map(name => {
      const bPersos = realBudgets.filter(b => b.owner === name);
      const firstBudget = bPersos[0];
      const total = calculatedResteChacun;

      const personalExpenses = realExpenses.filter(e => 
        e.category !== 'mensuel' && !e.category?.startsWith('mensuel') && e.category !== 'revenu' &&
        (
          (e.budget_id && bPersos.some(b => b.id === e.budget_id)) ||
          (firstBudget && e.budget_id === firstBudget.id) ||
          (e.category && e.category.toLowerCase() === name.toLowerCase()) ||
          (e.title && e.title.toLowerCase().includes(name.toLowerCase()))
        )
      );

      const spent = personalExpenses.filter(e => e.is_paid).reduce((sum, e) => sum + e.amount, 0);
      return { id: firstBudget?.id, name, total, spent, original: firstBudget };
    });

    const combinedPostes = activeMonth 
      ? realExpenses.filter(e => e.category === 'mensuel' || e.category?.startsWith('mensuel')).map(e => ({
          id: e.id,
          title: e.title,
          amount: e.amount,
          dueDate: e.due_date,
          isPaid: e.is_paid,
          isBudgetized: e.is_budgetized,
          isTemplate: false,
          category: e.category?.startsWith('mensuel_') ? e.category.split('_')[1] : null,
          originalExpense: e
        }))
      : defaultExpenses.map(e => ({ ...e, isTemplate: true }));

    const incomes = realExpenses.filter(e => e.category === 'revenu');
    const totalRevenus = incomes.length > 0 ? incomes.reduce((sum, e) => sum + e.amount, 0) : 5350;
    const dailyExpenses = realExpenses.filter(e => e.category !== 'mensuel' && !e.category?.startsWith('mensuel') && e.category !== 'revenu' && !e.is_budgetized);
    const totalDaily = dailyExpenses.reduce((sum, e) => sum + e.amount, 0);
    const paidMonthly = realExpenses.filter(e => (e.category === 'mensuel' || e.category?.startsWith('mensuel')) && e.is_paid).reduce((sum, e) => sum + e.amount, 0);
    const surLeCompte = realExpenses.filter(e => (e.category === 'mensuel' || e.category?.startsWith('mensuel')) && !e.is_paid).reduce((sum, e) => sum + e.amount, 0);

    return (
      <motion.div key="budgets" variants={pageVariants} initial="initial" animate="in" exit="out" className="space-y-6">
        {!activeMonth && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex items-center space-x-2.5"
          >
            <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
            <span className="text-[12px] text-[#a89ea6] leading-snug">
              Données issues de la trame par défaut (mois non initialisé).
            </span>
          </motion.div>
        )}

        {/* Toggle Budgets */}
        <motion.div variants={itemVariants} className="bg-[#171118] border border-[#2d2230] p-1.5 rounded-xl flex">
          <button 
            onClick={() => setBudgetTab('communs')}
            className={`flex-1 py-2 text-[14px] font-bold rounded-lg transition-colors flex items-center justify-center ${budgetTab === 'communs' ? 'bg-[#211824] text-white border border-[#3d2e40]' : 'text-[#a89ea6] hover:text-white'}`}
          >
            <Users className="w-4 h-4 mr-2" /> Communs
          </button>
          <button 
            onClick={() => setBudgetTab('persos')}
            className={`flex-1 py-2 text-[14px] font-bold rounded-lg transition-colors flex items-center justify-center ${budgetTab === 'persos' ? 'bg-[#211824] text-white border border-[#3d2e40]' : 'text-[#a89ea6] hover:text-white'}`}
          >
            <User className="w-4 h-4 mr-2" /> Personnels
          </button>
        </motion.div>

        {/* Dynamic Provision Card */}
        <motion.div 
          variants={itemVariants} 
          className="bg-[#171118] border border-[#2d2230] p-4 rounded-xl flex items-center justify-between shadow-inner"
        >
          <div className="flex items-center space-x-2">
            <CreditCard className="w-4 h-4 text-emerald-400" />
            <span className="text-[13px] text-[#a89ea6] font-medium">À prévoir sur le compte :</span>
          </div>
          <motion.span 
            key={surLeCompte}
            initial={{ scale: 0.85, opacity: 0.5 }}
            animate={{ scale: [1.15, 1], opacity: 1 }}
            transition={{ type: "spring", stiffness: 400, damping: 12 }}
            className="text-[16px] font-bold text-emerald-400 font-mono"
          >
            {surLeCompte.toFixed(2)} €
          </motion.span>
        </motion.div>

        <AnimatePresence mode="wait">
          {budgetTab === 'communs' ? (
              <motion.div key="postes" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="space-y-3">
                <div className="flex justify-between items-center mb-2">
                  <div className="relative flex-1 min-w-0 mr-2">
                    <div 
                      className="flex space-x-2 overflow-x-auto pb-1 pr-8 scrollbar-hide"
                      style={{
                        maskImage: 'linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) calc(100% - 32px), rgba(0,0,0,0) 100%)',
                        WebkitMaskImage: 'linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) calc(100% - 32px), rgba(0,0,0,0) 100%)'
                      }}
                    >
                      {['Toutes', 'À venir', 'Passées', 'Budgets'].map(f => (
                        <button 
                          key={f}
                          onClick={() => setPostesFilter(f as any)}
                          className={`px-3 py-1.5 rounded-lg text-[13px] font-bold whitespace-nowrap transition-colors ${postesFilter === f ? 'bg-[#ff154d] text-white shadow-lg shadow-[#ff154d]/20' : 'bg-[#171118] text-[#a89ea6] border border-[#2d2230]'}`}
                        >
                          {f}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button 
                    onClick={() => { setExpenseIsBudgetized(true); setExpenseIsPaid(false); setIsAddMonthlyPosteOpen(true); }} 
                    className="text-[#ff154d] hover:text-[#ff3b68] text-[13px] font-bold whitespace-nowrap shrink-0 ml-1 transition-colors"
                  >
                    + Ajouter
                  </button>
                </div>

                {combinedPostes.length === 0 ? (
                  <div className="text-center text-[#a89ea6] text-[14px] py-8 bg-[#171118] border border-[#2d2230] rounded-2xl flex flex-col items-center">
                    <Clock className="w-8 h-8 text-[#3d2e40] mb-3" />
                    Aucun poste de dépense pour le moment.
                  </div>
                ) : combinedPostes.filter(e => {
                  if (postesFilter === 'À venir') return !e.isPaid;
                  if (postesFilter === 'Passées') return e.isPaid;
                  if (postesFilter === 'Budgets') return e.isBudgetized !== false;
                  return true;
                }).map(expense => {
                  const usedAmount = expense.isTemplate ? realExpenses
                    .filter(tx => tx.budget_id === String(expense.id) && tx.is_paid && tx.id !== expense.id)
                    .reduce((sum, tx) => sum + Math.abs(tx.amount), 0) : (
                      expense.originalExpense?.budget_id
                        ? realExpenses
                            .filter(tx => tx.budget_id === expense.originalExpense.budget_id && tx.id !== expense.id && tx.category !== 'mensuel' && !tx.category?.startsWith('mensuel'))
                            .reduce((sum, tx) => sum + Math.abs(tx.amount), 0)
                        : 0
                    );
                  const provisioned = Number(expense.amount);
                  const progress = provisioned > 0 ? (usedAmount / provisioned) * 100 : 0;
                  const isOver = usedAmount > provisioned;
                  
                  return (
                    <div key={expense.id} className={`flex flex-col p-4 bg-[#171118] border border-[#2d2230] rounded-xl shadow-sm transition-opacity duration-300 ${expense.isPaid ? 'opacity-60' : ''}`}>
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <span className={`font-bold text-[15px] ${expense.isPaid ? 'text-[#a89ea6]' : 'text-white'}`}>{expense.title}</span>
                          <div className="flex items-center mt-1 space-x-2 text-[11px] font-medium">
                            <span className="text-[#a89ea6]">
                              {expense.dueDate ? (() => {
                                // Clean up if there is duplicated date strings in DB
                                let raw = expense.dueDate;
                                if (raw.length > 10 && raw.includes('-')) {
                                  const parts = raw.split('-');
                                  if (parts.length > 3) {
                                    raw = `${parts[0]}-${parts[1]}-${parts[parts.length - 1]}`;
                                  }
                                }
                                const d = new Date(raw);
                                return isNaN(d.getTime()) 
                                  ? `Le ${expense.dueDate}` 
                                  : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
                              })() : 'Le ?'}
                            </span>
                            {expense.category && (
                              <>
                                <span>•</span>
                                <span className="text-[#ff154d] bg-[#ff154d]/10 px-2 py-0.5 rounded border border-[#ff154d]/20 text-[10px] font-bold">{expense.category}</span>
                                <span>•</span>
                                <button 
                                  disabled={expense.isTemplate}
                                  onClick={async () => {
                                    if (expense.isTemplate) return;
                                    try {
                                      await updateExpense(expense.id, { is_paid: !expense.isPaid });
                                      refreshData(true);
                                      showToast(expense.isPaid ? "Poste marqué comme à venir" : "Poste marqué comme payé");
                                    } catch (err) {
                                      console.error(err);
                                    }
                                  }}
                                  className={`flex items-center px-1.5 py-0.5 rounded transition-colors ${expense.isPaid ? 'text-emerald-500 hover:bg-emerald-500/10' : 'text-[#ff154d] hover:bg-[#ff154d]/10'}`}
                                >
                                  {expense.isPaid ? (
                                    <><CheckCircle2 className="w-3 h-3 mr-1"/> Passé</>
                                  ) : (
                                    <><Clock className="w-3 h-3 mr-1"/> À venir</>
                                  )}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end">
                          <span className={`font-bold ${expense.isPaid ? 'text-[#a89ea6]' : 'text-[#ff154d]'}`} style={{ fontFamily: "'Lekton', monospace" }}>{provisioned.toFixed(2)} €</span>
                          {expense.isTemplate ? (
                            <button 
                              onClick={() => {
                                setEditingDefaultExpense(expense);
                                setIsDefaultExpenseModalOpen(true);
                              }}
                              className="mt-1 text-[#a89ea6] hover:text-white transition-colors"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                          ) : (
                            <div className="flex items-center space-x-2 mt-1">
                              <span className="text-[10px] text-[#ff154d] font-bold bg-[#ff154d]/10 px-2 py-0.5 rounded uppercase">Mensuel</span>
                              <button 
                                onClick={() => {
                                  setEditingMonthlyPoste(expense.originalExpense);
                                  const rawCategory = expense.originalExpense.category?.startsWith('mensuel_') ? expense.originalExpense.category.split('_')[1] : '';
                                  setNewMonthlyPosteForm({ 
                                    title: expense.title, 
                                    amount: expense.amount.toString(), 
                                    due_date: expense.dueDate || '',
                                    category: rawCategory
                                  });
                                  setExpenseIsBudgetized(expense.originalExpense.is_budgetized !== false);
                                  setExpenseIsPaid(expense.originalExpense.is_paid === true);
                                  setIsAddMonthlyPosteOpen(true);
                                }}
                                className="text-[#a89ea6] hover:text-white transition-colors"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button 
                                onClick={() => handleDeleteExpense(expense.id)}
                                className="text-[#a89ea6] hover:text-[#ff154d] transition-colors"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Jauge */}
                      {expense.isBudgetized !== false && expense.is_budgetized !== false && (
                        <>
                          <div className="w-full bg-[#211824] border border-[#3d2e40] rounded-full h-2.5 mb-1.5 overflow-hidden">
                            <div className={`h-full rounded-full transition-all duration-500 ${isOver ? 'bg-[#ff154d]' : 'bg-emerald-500'}`} style={{ width: `${Math.min(progress, 100)}%` }}></div>
                          </div>
                          <div className="flex justify-between text-[11px] font-bold text-[#8e848c]">
                            <span>{usedAmount.toFixed(2)} € utilisé</span>
                            <span>Reste : {(provisioned - usedAmount).toFixed(2)} €</span>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </motion.div>
          ) : (
            <motion.div key="persos" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="grid grid-cols-1 gap-4">
              <div className="flex justify-between items-center px-1 mb-2">
                <h3 className="text-white font-bold text-[15px]">Enveloppes Personnelles</h3>
              </div>
              {budgetsPersos.map((bp, idx) => {
                const percent = bp.total > 0 ? Math.min((bp.spent / bp.total) * 100, 100) : 0;
                const isWarning = percent > 85;
                return (
                  <div key={idx} onClick={() => setSelectedPerson(bp.name)} className="bg-[#171118] border border-[#2d2230] rounded-2xl p-5 relative overflow-hidden cursor-pointer active:scale-95 transition-transform">
                    <div className={`absolute top-0 right-0 w-16 h-16 blur-2xl rounded-full ${isWarning ? 'bg-[#ff154d]/20' : 'bg-purple-500/20'}`}></div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-[#211824] border border-[#3d2e40] rounded-full flex items-center justify-center mr-3">
                          <User className="w-5 h-5 text-[#a89ea6]" />
                        </div>
                        <h3 className="font-bold text-[18px] text-white">{bp.name}</h3>
                      </div>
                      <button onClick={(e) => {
                        e.stopPropagation();
                        if (bp.original) {
                          setEditingBudget(bp.original);
                          setNewBudgetForm({ name: bp.original.name, total: bp.total.toString(), type: 'perso', owner: bp.name });
                        } else {
                          setEditingBudget(null);
                          setNewBudgetForm({ name: bp.name, total: '0', type: 'perso', owner: bp.name });
                        }
                        setIsAddBudgetOpen(true);
                      }} className="text-[#a89ea6] hover:text-white transition-colors p-2">
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>
                    <h1 className="text-[36px] leading-none font-bold text-white mb-1" style={{ fontFamily: "'Lekton', monospace" }}>
                      {(bp.total - bp.spent).toFixed(2)} <span className="text-[24px]">€</span>
                    </h1>
                    <p className="text-[13px] font-bold text-[#a89ea6] mb-4">RESTANT SUR {bp.total.toFixed(2)}€</p>
                    <div className="h-1.5 w-full bg-[#211824] rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }} animate={{ width: `${percent}%` }} transition={{ duration: 1, ease: "easeOut" }}
                        className={`h-full rounded-full ${isWarning ? 'bg-[#ff154d]' : 'bg-emerald-500'}`}
                      />
                    </div>
                  </div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  const CalendarTab = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate(); // 31
    const firstDayOfWeek = new Date(year, month, 1).getDay(); // 0 is Sunday. In May 2026, May 1st is Friday (5).

    // Adjust for Monday as first day of week
    const startDay = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Friday becomes 4

    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    
    // Create empty cells for start of month
    const emptyCells = Array.from({ length: startDay }, (_, i) => i);

    return (
      <motion.div variants={pageVariants} initial="initial" animate="in" exit="out" className="space-y-6">
        <motion.div variants={itemVariants} className="bg-[#171118] border border-[#2d2230] rounded-2xl p-5 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-[18px] font-bold text-white flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-[#ff154d]"/> 
              Calendrier des Prélèvements
            </h2>
          </div>
          
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
              <div key={day} className="text-center text-[11px] font-bold text-[#8e848c] uppercase tracking-wider py-1">
                {day}
              </div>
            ))}
          </div>
          
          <div className="grid grid-cols-7 gap-1">
            {emptyCells.map(i => (
              <div key={`empty-${i}`} className="aspect-square rounded-xl bg-[#211824]/30 border border-transparent"></div>
            ))}
            
            {days.map(day => {
              // Find expenses that fall on this day
              const dayExpenses = defaultExpenses.filter(e => e.dueDate === day.toString());
              const hasExpenses = dayExpenses.length > 0;
              const isMultiple = dayExpenses.length > 1;
              const isToday = day === 12; // Mocking today's date as the 12th for the May 2026 prototype
              
              return (
                <div key={day} className={`aspect-square rounded-xl border flex flex-col items-center justify-center relative transition-all cursor-pointer hover:border-[#ff154d] ${hasExpenses ? 'bg-[#ff154d]/5 border-[#ff154d]/20' : 'bg-[#211824] border-[#3d2e40]'} ${isToday ? 'ring-2 ring-emerald-500 bg-emerald-500/10' : ''}`}>
                  <span className={`text-[14px] font-bold ${hasExpenses || isToday ? 'text-white' : 'text-[#a89ea6]'}`}>{day}</span>
                  
                  {hasExpenses && (
                    <div className="absolute bottom-1.5 flex space-x-0.5">
                      {isMultiple ? (
                        <div className="w-4 h-4 rounded-full bg-[#ff154d] text-white flex items-center justify-center text-[9px] font-bold shadow-[0_0_8px_rgba(255,21,77,0.6)]">
                          {dayExpenses.length}
                        </div>
                      ) : (
                        <div className="w-1.5 h-1.5 rounded-full bg-[#ff154d] shadow-[0_0_8px_rgba(255,21,77,0.6)]"></div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>
        
        <motion.div variants={itemVariants}>
          <div className="flex items-center mb-3 px-1">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 mr-2" />
            <h2 className="text-[16px] font-bold text-white">Prélèvements prévus</h2>
          </div>
          <div className="bg-[#171118] border border-[#2d2230] rounded-2xl overflow-hidden">
            {[...defaultExpenses].sort((a, b) => Number(a.dueDate) - Number(b.dueDate)).map((expense, idx) => (
              <div key={expense.id} className={`flex items-center justify-between p-3.5 ${idx !== defaultExpenses.length - 1 ? 'border-b border-[#2d2230]' : ''}`}>
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 bg-[#211824] border border-[#3d2e40] rounded-lg flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-[#a89ea6]" />
                  </div>
                  <div>
                    <p className="font-bold text-[15px] text-white">{expense.title}</p>
                    <p className="text-[13px] text-[#ff154d] font-medium">Le {expense.dueDate} du mois</p>
                  </div>
                </div>
                <div className="text-right font-bold text-[16px] text-white" style={{ fontFamily: "'Lekton', monospace" }}>
                  -{expense.amount.toFixed(2)} €
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    );
  };

  const AdvancesTab = () => {
    const pendingAdvances = realAdvances.filter(a => !a.settled);
    const settledAdvances = realAdvances.filter(a => a.settled);
    
    // Calculate emmanuelOwes and oceanneOwes based on realAdvances
    const emmanuelOwes = pendingAdvances.filter(a => (a.payer === 'Océane' || a.payer === 'Océanne') && a.for_whom === 'Emmanuel').reduce((sum, a) => sum + (a.amount - (a.repayments?.reduce((s:number,r:any)=>s+r.amount,0)||0)), 0) + pendingAdvances.filter(a => (a.payer === 'Océane' || a.payer === 'Océanne') && a.for_whom === 'Commun').reduce((sum, a) => sum + (a.amount - (a.repayments?.reduce((s:number,r:any)=>s+r.amount,0)||0))/2, 0);
    const oceanneOwes = pendingAdvances.filter(a => a.payer === 'Emmanuel' && (a.for_whom === 'Océane' || a.for_whom === 'Océanne')).reduce((sum, a) => sum + (a.amount - (a.repayments?.reduce((s:number,r:any)=>s+r.amount,0)||0)), 0) + pendingAdvances.filter(a => a.payer === 'Emmanuel' && a.for_whom === 'Commun').reduce((sum, a) => sum + (a.amount - (a.repayments?.reduce((s:number,r:any)=>s+r.amount,0)||0))/2, 0);
    const globalOwes = Math.abs(emmanuelOwes - oceanneOwes);
    const whoOwes = emmanuelOwes > oceanneOwes ? 'Emmanuel doit à Océane' : oceanneOwes > emmanuelOwes ? 'Océane doit à Emmanuel' : 'Comptes équilibrés (Net)';

    // Slider State & Calculation
    const [currentSlide, setCurrentSlide] = useState(0);
    const slides = [
      {
        id: 'global',
        title: whoOwes,
        amount: globalOwes,
        action: "Régler l'ardoise globale",
        isGlobal: true
      }
    ];

    const detailedDebts: Record<string, number> = {};
    pendingAdvances.forEach(a => {
      const remaining = a.amount - (a.repayments?.reduce((s:number,r:any)=>s+r.amount,0)||0);
      if (remaining > 0) {
        const key = `${a.for_whom} doit à ${a.payer}`;
        detailedDebts[key] = (detailedDebts[key] || 0) + remaining;
      }
    });

    Object.entries(detailedDebts).forEach(([key, amount], index) => {
      slides.push({
        id: `detail-${index}`,
        title: key,
        amount: amount,
        action: "Régler cette dette",
        isGlobal: false
      });
    });

    const activeSlide = slides[currentSlide] || slides[0];

    return (
      <motion.div variants={pageVariants} initial="initial" animate="in" exit="out" className="space-y-6">
        <motion.div variants={itemVariants} className="relative">
          <div className={`bg-[#171118] border ${activeSlide.isGlobal ? 'border-[#ff154d]' : 'border-[#2d2230]'} rounded-2xl p-6 shadow-[0_0_15px_rgba(255,21,77,0.15)] relative overflow-hidden transition-colors`}>
            {activeSlide.isGlobal && <div className="absolute top-0 right-0 w-32 h-32 bg-[#ff154d]/10 blur-3xl rounded-full"></div>}
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <p className={`text-[15px] font-bold mb-1 uppercase tracking-wider ${activeSlide.isGlobal ? 'text-[#a89ea6]' : 'text-[#8e848c]'}`}>{activeSlide.title}</p>
                <h1 className="text-[42px] leading-none font-bold text-white" style={{ fontFamily: "'Lekton', monospace" }}>
                  {activeSlide.amount.toFixed(2)} <span className="text-[28px]">€</span>
                </h1>
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${activeSlide.isGlobal ? 'bg-[#ff154d]/10 border border-[#ff154d]/30 text-[#ff154d]' : 'bg-[#211824] border border-[#3d2e40] text-[#a89ea6]'}`}>
                <ArrowRightLeft className="w-6 h-6" />
              </div>
            </div>
            <button 
              onClick={() => handleSettleDebt(activeSlide)}
              className="w-full mt-6 bg-[#211824] hover:bg-[#2d2230] text-white text-[15px] font-bold py-3.5 rounded-xl transition-all border border-[#3d2e40] active:scale-95"
            >
              {activeSlide.action}
            </button>
          </div>
          
          {/* Slider Dots */}
          {slides.length > 1 && (
            <div className="flex justify-center items-center mt-3 space-x-2">
              {slides.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${currentSlide === idx ? 'bg-[#ff154d] w-6' : 'bg-[#3d2e40] hover:bg-[#a89ea6]'}`}
                />
              ))}
            </div>
          )}
        </motion.div>

        <motion.div variants={itemVariants}>
          <div className="flex justify-between items-center mb-3 px-1">
            <h2 className="text-[16px] font-bold text-white flex items-center"><Clock className="w-4 h-4 mr-1.5 text-[#ff154d]"/> Avances en cours</h2>
            <button onClick={() => { setEditingAdvance(null); setIsAddAdvanceOpen(true); }} className="text-[13px] font-bold text-[#ff154d] bg-[#ff154d]/10 px-3 py-1.5 rounded-lg border border-[#ff154d]/30 flex items-center">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </button>
          </div>
          <div className="bg-[#171118] border border-[#2d2230] rounded-2xl overflow-hidden mb-6">
            {pendingAdvances.map((adv, idx) => {
              const totalRepaid = adv.repayments?.reduce((s:number,r:any)=>s+r.amount,0) || 0;
              const remaining = adv.amount - totalRepaid;
              return (
              <div key={adv.id} onClick={() => setSelectedAdvance(adv)} className={`p-4 hover:bg-[#211824]/30 cursor-pointer transition-colors ${idx !== pendingAdvances.length - 1 ? 'border-b border-[#2d2230]' : ''}`}>
                <div className="flex justify-between items-start mb-1.5">
                  <p className="font-bold text-[15px] text-white">{adv.reason}</p>
                  <p className="font-bold text-[16px] text-[#ff154d]" style={{ fontFamily: "'Lekton', monospace" }}>
                    {adv.amount.toFixed(2)} €
                  </p>
                </div>
                {totalRepaid > 0 && (
                  <div className="mb-2">
                    <div className="flex justify-between text-[11px] text-[#a89ea6] mb-1">
                      <span>Remboursé: {totalRepaid.toFixed(2)} €</span>
                      <span>Reste: {remaining.toFixed(2)} €</span>
                    </div>
                    <div className="w-full bg-[#211824] rounded-full h-1.5 border border-[#3d2e40]">
                      <div className="bg-[#ff154d] h-1.5 rounded-full" style={{ width: `${(totalRepaid / adv.amount) * 100}%` }}></div>
                    </div>
                  </div>
                )}
                <div className="flex justify-between items-center mt-2">
                  <p className="text-[13px] text-[#8e848c] font-medium flex items-center">
                    <span className="text-white font-bold bg-[#211824] px-2 py-0.5 rounded border border-[#3d2e40] mr-2">{adv.payer} a payé</span>
                    <span>pour <span className="text-white font-bold bg-[#211824] px-2 py-0.5 rounded border border-[#3d2e40] ml-1">{adv.for_whom}</span></span>
                  </p>
                  <div className="flex gap-2">
                    <button onClick={(e) => { 
                      e.stopPropagation();
                      setEditingAdvance(adv as any); 
                      setNewAdvanceForm({
                        payer: adv.payer,
                        forWhom: adv.for_whom,
                        reason: adv.reason,
                        amount: adv.amount.toString()
                      });
                      setIsAddAdvanceOpen(true); 
                    }} className="text-[12px] font-bold text-[#a89ea6] bg-[#211824] border border-[#3d2e40] px-2 py-1 rounded-lg hover:text-white transition-colors flex items-center">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteAdvance(adv.id); }} className="text-[12px] font-bold text-[#a89ea6] bg-[#211824] border border-[#3d2e40] px-2 py-1 rounded-lg hover:text-[#ff154d] hover:border-[#ff154d]/50 transition-colors flex items-center">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={(e) => {
                      e.stopPropagation();
                      setRepayingAdvance(adv as any);
                      const remaining = adv.amount - totalRepaid;
                      setRepaymentAmount(remaining.toString());
                    }} className="text-[12px] font-bold text-[#ff154d] bg-[#ff154d]/10 border border-[#ff154d]/30 px-2 py-1 rounded-lg hover:bg-[#ff154d]/20 transition-colors">
                      Rembourser
                    </button>
                  </div>
                </div>
              </div>
            )})}
            {pendingAdvances.length === 0 && (
              <div className="p-4 text-center text-[#a89ea6] text-[14px]">Aucune avance en cours.</div>
            )}
          </div>

          <h2 className="text-[16px] font-bold text-[#a89ea6] mb-3 px-1 flex items-center"><CheckCircle2 className="w-4 h-4 mr-1.5 text-emerald-500"/> Déjà réglées</h2>
          <div className="bg-[#171118] border border-[#2d2230] rounded-2xl overflow-hidden opacity-75">
            {settledAdvances.length === 0 ? (
              <div className="p-4 text-center text-[#a89ea6] text-[14px]">Aucune avance réglée.</div>
            ) : settledAdvances.map((adv, idx) => (
              <div key={adv.id} onClick={() => setSelectedAdvance(adv)} className={`p-4 hover:bg-[#211824]/30 cursor-pointer transition-colors ${idx !== settledAdvances.length - 1 ? 'border-b border-[#2d2230]' : ''}`}>
                <div className="flex justify-between items-start mb-1.5">
                  <p className="font-bold text-[15px] text-white line-through decoration-[#a89ea6]">{adv.reason}</p>
                  <p className="font-bold text-[16px] text-[#a89ea6]" style={{ fontFamily: "'Lekton', monospace" }}>
                    {adv.amount.toFixed(2)} €
                  </p>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <p className="text-[13px] text-[#8e848c] font-medium flex items-center">
                    <span className="text-white bg-[#211824] px-2 py-0.5 rounded border border-[#3d2e40] mr-2">{adv.payer} a payé</span>
                    <span>pour <span className="text-white bg-[#211824] px-2 py-0.5 rounded border border-[#3d2e40] ml-1">{adv.for_whom}</span></span>
                  </p>
                  <span className="text-[11px] font-bold text-emerald-500 uppercase tracking-wider bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">Remboursé</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    );
  };
  const AnalyticsTab = () => {
    const [analyticsLoading, setAnalyticsLoading] = useState(true);
    const [history, setHistory] = useState<{ monthName: string; value: number }[]>([]);
    const [currentDistribution, setCurrentDistribution] = useState<{
      fixes: number;
      envelopes: number;
      daily: number;
      rest: number;
      total: number;
    } | null>(null);

    React.useEffect(() => {
      const loadAnalytics = async () => {
        setAnalyticsLoading(true);
        try {
          const data = await getAnalyticsData();
          
          // 1. Calcul de l'évolution historique du reste à vivre (derniers 6 mois)
          const monthsList = data.months.slice(-6); // Garder les 6 derniers mois
          const historyData = monthsList.map(m => {
            const mExpenses = data.expenses.filter(e => e.month_id === m.id);
            const mBudgets = data.budgets.filter(b => b.month_id === m.id);
            
            const incomes = mExpenses.filter(e => e.category === 'revenu');
            const totalRevenus = incomes.length > 0 ? incomes.reduce((sum, e) => sum + e.amount, 0) : 5350;
            
            const monthlyBills = mExpenses.filter(e => e.category === 'mensuel' || e.category?.startsWith('mensuel'));
            const totalMensuel = monthlyBills.reduce((sum, e) => sum + e.amount, 0);
            
            const resteAVivre = Math.max(0, totalRevenus - totalMensuel);
            
            const [year, monthNum] = m.month_key.split('-');
            const date = new Date(Number(year), Number(monthNum) - 1, 1);
            const rawName = date.toLocaleDateString('fr-FR', { month: 'short' });
            const monthName = rawName.charAt(0).toUpperCase() + rawName.slice(1).replace('.', '');
            
            return {
              monthName,
              value: resteAVivre
            };
          });
          setHistory(historyData);
          
          // 2. Calcul de la distribution réelle du mois actif
          if (activeMonth) {
            const activeExpenses = data.expenses.filter(e => e.month_id === activeMonth.id);
            const activeBudgets = data.budgets.filter(b => b.month_id === activeMonth.id);
            
            const incomes = activeExpenses.filter(e => e.category === 'revenu');
            const totalRevenus = incomes.length > 0 ? incomes.reduce((sum, e) => sum + e.amount, 0) : 5350;
            
            const monthlyBills = activeExpenses.filter(e => e.category === 'mensuel' || e.category?.startsWith('mensuel'));
            const totalMensuel = monthlyBills.reduce((sum, e) => sum + e.amount, 0);
            
            const budgetizedExpenses = activeExpenses.filter(e => (e.category === 'mensuel' || e.category?.startsWith('mensuel')) && e.is_budgetized !== false);
            const totalEnvelopes = budgetizedExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
            const totalFixesOnly = Math.max(0, totalMensuel - totalEnvelopes);
            
            const dailyExpenses = activeExpenses.filter(e => e.category !== 'mensuel' && !e.category?.startsWith('mensuel') && e.category !== 'revenu' && !e.is_budgetized);
            const totalDaily = dailyExpenses.reduce((sum, e) => sum + e.amount, 0);
            
            const resteAVivre = Math.max(0, totalRevenus - totalMensuel - totalDaily);
            
            setCurrentDistribution({
              fixes: totalFixesOnly,
              envelopes: totalEnvelopes,
              daily: totalDaily,
              rest: resteAVivre,
              total: totalRevenus
            });
          } else {
            setCurrentDistribution(null);
          }
        } catch (err) {
          console.error("Erreur lors du chargement des analyses:", err);
        } finally {
          setAnalyticsLoading(false);
        }
      };
      
      loadAnalytics();
    }, [activeMonth]);

    if (analyticsLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-20 text-[#a89ea6]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ff154d] mb-4"></div>
          <span>Chargement des analyses...</span>
        </div>
      );
    }

    const maxVal = Math.max(...history.map(h => h.value), 100);

    const fixesPct = currentDistribution && currentDistribution.total > 0 ? Math.round((currentDistribution.fixes / currentDistribution.total) * 100) : 0;
    const envelopesPct = currentDistribution && currentDistribution.total > 0 ? Math.round((currentDistribution.envelopes / currentDistribution.total) * 100) : 0;
    const dailyPct = currentDistribution && currentDistribution.total > 0 ? Math.round((currentDistribution.daily / currentDistribution.total) * 100) : 0;
    const restPct = currentDistribution && currentDistribution.total > 0 ? Math.max(0, 100 - fixesPct - envelopesPct - dailyPct) : 100;

    return (
      <motion.div variants={pageVariants} initial="initial" animate="in" exit="out" className="space-y-6">
        <motion.div variants={itemVariants} className="bg-[#171118] border border-[#2d2230] rounded-2xl p-5 shadow-lg">
          <h2 className="text-[18px] font-bold text-white flex items-center mb-6">
            <TrendingUp className="w-5 h-5 mr-2 text-[#ff154d]"/> 
            Évolution du Reste à Vivre
          </h2>
          {history.length === 0 ? (
            <div className="text-center py-10 text-[#a89ea6] text-[13px]">
              Aucun mois enregistré dans l'historique.
            </div>
          ) : (
            <div className="flex items-end justify-around h-44 mt-4 px-2">
              {history.map((data, idx) => (
                <div key={idx} className="flex flex-col items-center w-12 group relative">
                  <span className="text-[10px] font-bold text-white mb-1.5 opacity-0 group-hover:opacity-100 transition-opacity absolute -top-5 bg-[#211824] px-1.5 py-0.5 rounded border border-[#3d2e40] whitespace-nowrap z-10">
                    {data.value.toFixed(0)} €
                  </span>
                  <div className="w-full bg-[#211824]/50 border border-[#3d2e40]/50 rounded-t-lg flex items-end justify-center overflow-hidden" style={{ height: '120px' }}>
                    <motion.div 
                      initial={{ height: 0 }} 
                      animate={{ height: `${(data.value / maxVal) * 120}px` }} 
                      transition={{ duration: 1, delay: idx * 0.08 }}
                      className={`w-full rounded-t-lg ${idx === history.length - 1 ? 'bg-[#ff154d] shadow-[0_0_10px_rgba(255,21,77,0.5)]' : 'bg-emerald-500/80'}`}
                    />
                  </div>
                  <span className={`text-[10px] font-bold mt-2.5 ${idx === history.length - 1 ? 'text-[#ff154d]' : 'text-[#a89ea6]'}`}>{data.monthName}</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div variants={itemVariants} className="bg-[#171118] border border-[#2d2230] rounded-2xl p-5 shadow-lg">
          <h2 className="text-[18px] font-bold text-white flex items-center mb-6">
            <PieChart className="w-5 h-5 mr-2 text-[#a89ea6]"/> 
            Synthèse du mois ({new Intl.DateTimeFormat('fr-FR', { month: 'long' }).format(currentDate)})
          </h2>
          {currentDistribution ? (
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-[13px] font-bold mb-1.5">
                  <span className="text-white">Dépenses Fixes (Prélèvements)</span>
                  <span className="text-[#a89ea6]">{currentDistribution.fixes.toFixed(2)} € ({fixesPct}%)</span>
                </div>
                <div className="w-full bg-[#211824] rounded-full h-2">
                  <div className="bg-purple-500 h-2 rounded-full transition-all duration-1000" style={{ width: `${fixesPct}%` }}></div>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between text-[13px] font-bold mb-1.5">
                  <span className="text-white">Enveloppes Budgets</span>
                  <span className="text-[#a89ea6]">{currentDistribution.envelopes.toFixed(2)} € ({envelopesPct}%)</span>
                </div>
                <div className="w-full bg-[#211824] rounded-full h-2">
                  <div className="bg-blue-400 h-2 rounded-full transition-all duration-1000" style={{ width: `${envelopesPct}%` }}></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between text-[13px] font-bold mb-1.5">
                  <span className="text-white">Dépenses Quotidiennes (Hors Budget)</span>
                  <span className="text-[#a89ea6]">{currentDistribution.daily.toFixed(2)} € ({dailyPct}%)</span>
                </div>
                <div className="w-full bg-[#211824] rounded-full h-2">
                  <div className="bg-amber-500 h-2 rounded-full transition-all duration-1000" style={{ width: `${dailyPct}%` }}></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between text-[13px] font-bold mb-1.5">
                  <span className="text-[#ff154d]">Reste à Vivre</span>
                  <span className="text-[#ff154d]">{currentDistribution.rest.toFixed(2)} € ({restPct}%)</span>
                </div>
                <div className="w-full bg-[#211824] rounded-full h-2">
                  <div className="bg-[#ff154d] h-2 rounded-full transition-all duration-1000" style={{ width: `${restPct}%` }}></div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-[#a89ea6] text-[13px]">
              Aucune donnée pour ce mois.
            </div>
          )}
        </motion.div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-[#110a10] text-[#f2f0f2] pb-32 selection:bg-[#ff154d]/30 relative overflow-hidden" style={{ fontFamily: "'Oxanium', sans-serif" }}>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Lekton:wght@400;700&family=Oxanium:wght@400;500;600;700;800&display=swap');
      </style>
      
      {/* Background ambient radial gradient */}
      <div className="fixed inset-0 pointer-events-none z-0" style={{ backgroundImage: 'radial-gradient(circle at 100% 0, #ff154d1a 0%, #0000 40%), radial-gradient(circle at 0 100%, #ff4b721a 0%, #0000 40%)' }}></div>

      {/* Header with Month Navigation */}
      <header className="fixed top-0 left-0 w-full z-[80] px-5 pb-4 flex flex-col space-y-4 bg-[#110a10] shadow-[0_10px_30px_rgba(0,0,0,0.5)]" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 16px)' }}>
        <div className="flex justify-between items-center w-full max-w-lg mx-auto">
          <span 
            className="font-bold text-[22px] tracking-tight text-white flex items-center cursor-pointer"
            onClick={() => setActiveTab('home')}
          >
            Zeyo<span className="text-[#ff154d] ml-1">✧</span>
          </span>
          <div className="flex space-x-5">
            <button onClick={() => setIsSettingsOpen(true)} className="text-[#a89ea6] hover:text-white transition-colors">
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Month Selector Pill */}
        <div className="flex justify-between items-center bg-[#171118] border border-[#2d2230] rounded-full p-1 w-full max-w-xs mx-auto shadow-inner">
          <button onClick={() => setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1))} className="p-1.5 text-[#a89ea6] hover:text-white hover:bg-[#211824] rounded-full transition-colors"><ChevronLeft className="w-5 h-5" /></button>
          <span className="font-bold text-[15px] text-white tracking-wide uppercase">
            {new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(currentDate)}
          </span>
          <button onClick={() => setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1))} className="p-1.5 text-[#a89ea6] hover:text-white hover:bg-[#211824] rounded-full transition-colors"><ChevronRight className="w-5 h-5" /></button>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="relative z-10 w-full px-5 pb-5 max-w-lg mx-auto" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 140px)' }}>
        <AnimatePresence mode="wait">
          {activeTab === 'home' && <HomeTab />}
          {activeTab === 'calendar' && <CalendarTab />}
          {activeTab === 'expenses' && <ExpensesTab />}
          {activeTab === 'budgets' && <BudgetsTab />}
          {activeTab === 'advances' && <AdvancesTab />}
          {activeTab === 'analytics' && <AnalyticsTab />}
        </AnimatePresence>
      </main>

      {/* Floating Bottom Dock */}
      <div 
        className="fixed left-1/2 transform -translate-x-1/2 w-[calc(100%-16px)] max-w-[430px] z-50"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 14px)' }}
      >
        <motion.div 
          initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2, type: "spring", damping: 20 }}
          className="bg-[#171118]/95 backdrop-blur-xl border border-[#2d2230] rounded-2xl p-1 flex justify-between shadow-2xl"
        >
          {[
            { id: 'home', icon: Home, label: 'Aperçu' },
            { id: 'budgets', icon: PieChart, label: 'Budgets' },
            { id: 'expenses', icon: CreditCard, label: 'Dépenses' },
            { id: 'advances', icon: ArrowRightLeft, label: 'Avances' },
            { id: 'calendar', icon: Calendar, label: 'Calendrier' },
            { id: 'analytics', icon: TrendingUp, label: 'Analyse' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className="relative py-1.5 px-0.5 sm:py-2 sm:px-1 rounded-xl transition-all flex flex-col items-center justify-center flex-1 min-w-0 group"
            >
              {activeTab === item.id && (
                <motion.div 
                  layoutId="active-pill"
                  className="absolute inset-0 bg-[#211824] rounded-xl border border-[#3d2e40]"
                  transition={{ type: "spring", stiffness: 500, damping: 35 }}
                />
              )}
              <item.icon className={`w-[18px] h-[18px] sm:w-[21px] sm:h-[21px] relative z-10 transition-colors mb-1 ${activeTab === item.id ? 'text-white' : 'text-[#8e848c] group-hover:text-white'}`} />
              <span className={`text-[8px] sm:text-[9.5px] font-bold relative z-10 transition-colors uppercase tracking-tight text-center truncate w-full px-0.5 ${activeTab === item.id ? 'text-white' : 'text-[#8e848c] group-hover:text-white'}`}>
                {item.label}
              </span>
            </button>
          ))}
        </motion.div>
      </div>

      {/* Overlays / Modals */}
        {isAddBudgetOpen && (
          <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm">
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25, stiffness: 300 }} className="w-full max-w-lg bg-[#171118] rounded-t-3xl border-t border-[#2d2230] p-6 pb-12 shadow-[0_-10px_40px_rgba(0,0,0,0.5)]">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-[20px] font-bold text-white">{editingBudget ? 'Modifier l\'enveloppe' : 'Ajouter une enveloppe'}</h2>
                <button onClick={() => { setIsAddBudgetOpen(false); setEditingBudget(null); }} className="p-2 bg-[#211824] rounded-full text-[#a89ea6]"><X className="w-5 h-5"/></button>
              </div>
              <div className="space-y-4">
                {newBudgetForm.type === 'perso' && (
                  <div>
                    <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-2 block">Pour qui ?</label>
                    <div className="grid grid-cols-2 gap-2 p-1 bg-[#211824] border border-[#3d2e40] rounded-xl mb-4">
                      <button onClick={() => setNewBudgetForm({...newBudgetForm, owner: 'Emmanuel'})} className={`${newBudgetForm.owner === 'Emmanuel' ? 'bg-[#171118] text-white shadow-sm' : 'text-[#a89ea6] hover:text-white transition-colors'} py-2 rounded-lg font-bold text-[13px]`}>Emmanuel</button>
                      <button onClick={() => setNewBudgetForm({...newBudgetForm, owner: 'Océane'})} className={`${newBudgetForm.owner === 'Océane' ? 'bg-[#171118] text-white shadow-sm' : 'text-[#a89ea6] hover:text-white transition-colors'} py-2 rounded-lg font-bold text-[13px]`}>Océane</button>
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Nom de l'enveloppe</label>
                  <select value={newBudgetForm.name} onChange={(e) => setNewBudgetForm({...newBudgetForm, name: e.target.value})} className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#ff154d] appearance-none">
                    <option value="" disabled>Sélectionner une catégorie</option>
                    {expenseCategories.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Montant global alloué (€)</label>
                  <input type="number" value={newBudgetForm.total} onChange={(e) => setNewBudgetForm({...newBudgetForm, total: e.target.value})} placeholder="0.00" className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white font-bold text-[18px] focus:outline-none focus:border-[#ff154d]" style={{ fontFamily: "'Lekton', monospace" }} />
                </div>

                <button onClick={handleAddBudget} className="w-full mt-4 bg-purple-500 hover:bg-purple-600 text-white font-bold py-4 rounded-xl shadow-[0_0_15px_rgba(168,85,247,0.4)] transition-all">
                  {editingBudget ? 'Sauvegarder' : 'Créer l\'enveloppe'}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {isAddMonthlyPosteOpen && (
          <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm">
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25, stiffness: 300 }} className="w-full max-w-lg bg-[#171118] rounded-t-3xl border-t border-[#2d2230] p-6 pb-12 shadow-[0_-10px_40px_rgba(0,0,0,0.5)]">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-[20px] font-bold text-white">{editingMonthlyPoste ? 'Modifier le poste' : 'Ajouter un poste mensuel'}</h2>
                <button onClick={() => { setIsAddMonthlyPosteOpen(false); setEditingMonthlyPoste(null); setNewMonthlyPosteForm({ title: '', amount: '', due_date: '', category: '' }); setExpenseIsPaid(false); }} className="p-2 bg-[#211824] rounded-full text-[#a89ea6]"><X className="w-5 h-5"/></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Titre du poste</label>
                  <input type="text" value={newMonthlyPosteForm.title} onChange={(e) => setNewMonthlyPosteForm({...newMonthlyPosteForm, title: e.target.value})} placeholder="Ex: Électricité (EDF)" className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#ff154d]" />
                </div>
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Catégorie</label>
                  <select value={newMonthlyPosteForm.category} onChange={(e) => setNewMonthlyPosteForm({...newMonthlyPosteForm, category: e.target.value})} className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#ff154d] appearance-none">
                    <option value="" disabled>Sélectionner une catégorie</option>
                    {expenseCategories.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Montant (€)</label>
                  <input type="number" value={newMonthlyPosteForm.amount} onChange={(e) => setNewMonthlyPosteForm({...newMonthlyPosteForm, amount: e.target.value})} placeholder="0.00" className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white font-bold text-[18px] focus:outline-none focus:border-[#ff154d]" style={{ fontFamily: "'Lekton', monospace" }} />
                </div>
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Jour prévu (1-31)</label>
                  <input type="number" min="1" max="31" placeholder="Ex: 5" value={newMonthlyPosteForm.due_date} onChange={(e) => setNewMonthlyPosteForm({...newMonthlyPosteForm, due_date: e.target.value})} className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white font-bold focus:outline-none focus:border-[#ff154d]" style={{ fontFamily: "'Lekton', monospace" }} />
                </div>
                <div className="flex justify-between items-center p-3.5 bg-[#211824] border border-[#3d2e40] rounded-xl">
                  <div className="flex flex-col">
                    <span className="text-white font-bold text-[14px]">Budgétiser le poste</span>
                    <span className="text-[#a89ea6] text-[11px] mt-0.5">L'inclure dans les jauges et enveloppes budgétaires</span>
                  </div>
                  <button 
                    onClick={() => setExpenseIsBudgetized(!expenseIsBudgetized)}
                    className={`w-12 h-6 rounded-full p-1 flex items-center transition-colors ${expenseIsBudgetized ? 'bg-[#ff154d] justify-end' : 'bg-[#2d2230] justify-start'}`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full shadow-sm"></div>
                  </button>
                </div>
                <div className="flex justify-between items-center p-3.5 bg-[#211824] border border-[#3d2e40] rounded-xl">
                  <div className="flex flex-col">
                    <span className="text-white font-bold text-[14px]">Marquer comme prélevé (passé)</span>
                    <span className="text-[#a89ea6] text-[11px] mt-0.5">Le montant a déjà été débité du compte</span>
                  </div>
                  <button 
                    onClick={() => setExpenseIsPaid(!expenseIsPaid)}
                    className={`w-12 h-6 rounded-full p-1 flex items-center transition-colors ${expenseIsPaid ? 'bg-emerald-500 justify-end' : 'bg-[#2d2230] justify-start'}`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full shadow-sm"></div>
                  </button>
                </div>

                <button onClick={handleAddMonthlyPoste} className="w-full mt-4 bg-[#ff154d] hover:bg-[#ff3b68] text-white font-bold py-4 rounded-xl shadow-[0_0_15px_rgba(255,21,77,0.3)] transition-all">
                  Enregistrer le poste
                </button>
              </div>
            </motion.div>
          </div>
        )}

      <AnimatePresence>
        {isSettingsOpen && (
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }} className="fixed inset-0 z-[90] bg-[#110a10] flex flex-col" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 20px)' }}>
            <div className="p-5 overflow-y-auto pb-32 max-w-lg mx-auto w-full">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-[24px] font-bold text-white">Paramètres</h2>
                <button onClick={() => setIsSettingsOpen(false)} className="p-2 bg-[#211824] rounded-full text-[#a89ea6]"><X className="w-5 h-5"/></button>
              </div>
              
              {/* Tab Navigation */}
              <div className="flex space-x-1.5 overflow-x-auto pb-3 mb-6 scrollbar-none border-b border-[#2d2230]/50">
                {[
                  { id: 'trame', label: 'Trame & Sim.', icon: Calendar },
                  { id: 'sources', label: 'Sources', icon: CreditCard },
                  { id: 'categories', label: 'Catégories', icon: PieChart },
                  { id: 'compte', label: 'Mon Compte', icon: User }
                ].map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeSettingsTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveSettingsTab(tab.id as any)}
                      className={`flex items-center space-x-1.5 px-3 py-2 rounded-xl text-[12px] font-bold transition-all shrink-0 ${
                        isActive 
                          ? 'bg-[#ff154d] text-white shadow-[0_0_12px_rgba(255,21,77,0.3)]' 
                          : 'bg-[#1c141d] border border-[#2d2230] text-[#a89ea6] hover:text-white hover:bg-[#2d2230]/30'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>

              {activeSettingsTab === 'trame' && (
                <>
                  {/* Mois Actif Settings */}
                  <div className="bg-[#171118] border border-[#2d2230] rounded-3xl p-6 mb-6 shadow-2xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-[#ff154d]/5 rounded-full blur-2xl pointer-events-none"></div>
                    
                    <h3 className="font-bold text-[16px] text-white mb-1.5 flex items-center">
                      <Calendar className="w-5 h-5 mr-2.5 text-[#ff154d]" />
                      Mois actif par défaut
                    </h3>
                    <p className="text-[12.5px] text-[#a89ea6] mb-5 leading-relaxed">
                      Sélectionnez le mois qui sera affiché automatiquement lors du chargement de l'application.
                    </p>
                    
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-[#ff154d]">
                        <Calendar className="w-4 h-4" />
                      </div>
                      <input 
                        type="month" 
                        value={`${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`}
                        onChange={async (e) => {
                          const val = e.target.value;
                          if (!val) return;
                          const [year, month] = val.split('-');
                          const newDate = new Date(Number(year), Number(month) - 1, 1);
                          setCurrentDate(newDate);
                          
                          try {
                            let m = await getMonth(val);
                            if (!m) {
                              m = await createMonth({
                                month_key: val,
                                reste_a_vivre: 0,
                                sur_le_compte: 0,
                                is_active: false
                              });
                            }
                            if (m) {
                              await setActiveMonthInDB(m.id);
                              showToast("Mois actif défini !");
                              const formattedMonth = newDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
                              triggerInstantNotification(
                                "Mois actif modifié 📅",
                                `Le mois de ${formattedMonth} est désormais défini comme mois actif.`
                              );
                            }
                            refreshData();
                          } catch (err) {
                            console.error("Erreur de modification du mois actif:", err);
                            alert("Impossible de modifier le mois actif.");
                          }
                        }}
                        className="w-full bg-[#211824] border border-[#3d2e40] hover:border-[#ff154d]/50 focus:border-[#ff154d] rounded-2xl pl-11 pr-10 py-3.5 text-white text-[14px] font-bold focus:outline-none transition-all appearance-none cursor-pointer"
                      />
                      <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-[#a89ea6]">
                        <ChevronRight className="w-4 h-4" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#171118] border border-[#2d2230] rounded-2xl p-5 mb-6">
                    <h3 className="font-bold text-white mb-2 flex items-center"><Calendar className="w-5 h-5 mr-2 text-[#ff154d]"/> Trame des dépenses par défaut</h3>
                    <p className="text-[13px] text-[#8e848c] mb-5 leading-relaxed">
                      Définissez les postes de dépenses communs avec leur montant habituel. Lors de la création d'un nouveau mois, ces dépenses seront générées automatiquement.
                    </p>

                    <div className="space-y-2 mb-4">
                      {defaultExpenses.map(expense => (
                        <div key={expense.id} className="flex justify-between items-center p-3 bg-[#211824] border border-[#3d2e40] rounded-xl">
                          <div className="flex flex-col">
                            <span className="text-white font-medium text-[14px]">{expense.title}</span>
                            <span className={`text-[10px] uppercase font-bold mt-0.5 ${expense.isBudgetized !== false ? 'text-[#ff154d]' : 'text-[#8e848c]'}`}>
                              {expense.isBudgetized !== false ? 'Budgétisé' : 'Non budgétisé'}
                            </span>
                          </div>
                          <div className="flex items-center space-x-3">
                            <span className="text-white font-bold" style={{ fontFamily: "'Lekton', monospace" }}>{expense.amount.toFixed(2)} €</span>
                            <button 
                              onClick={() => {
                                setEditingDefaultExpense(expense);
                                setIsDefaultExpenseModalOpen(true);
                              }}
                              className="text-[#a89ea6] hover:text-white transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={async () => {
                                if (!window.confirm("Voulez-vous supprimer ce poste de la trame par défaut ?")) return;
                                const updatedList = defaultExpenses.filter(e => e.id !== expense.id);
                                setDefaultExpenses(updatedList);
                                await saveDefaultExpensesToSupabase(updatedList);
                              }}
                              className="text-[#a89ea6] hover:text-[#ff154d] transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {defaultExpenses.length > 0 && (
                      <div className="flex justify-between items-center p-3 bg-[#1c141d]/50 border border-[#2d2230]/70 rounded-xl mb-4">
                        <span className="text-[#a89ea6] font-medium text-[13px]">Total indicatif :</span>
                        <span className="text-white font-bold text-[15px]" style={{ fontFamily: "'Lekton', monospace" }}>
                          {defaultExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0).toFixed(2)} €
                        </span>
                      </div>
                    )}
                    
                    <button 
                      onClick={() => {
                        setEditingDefaultExpense({ id: 0, title: '', amount: '' });
                        setIsDefaultExpenseModalOpen(true);
                      }}
                      className="w-full mb-4 text-[13px] font-bold text-[#ff154d] bg-[#ff154d]/10 py-3 rounded-xl border border-[#ff154d]/30 flex items-center justify-center transition-all hover:bg-[#ff154d]/20"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Ajouter un poste
                    </button>
                    
                    <div className="flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                      <span className="text-[13px] font-bold text-emerald-400">Export automatique activé</span>
                      <div className="w-10 h-5 bg-emerald-500 rounded-full flex justify-end p-0.5">
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </div>
                    </div>
                  </div>

                  {/* Simulateur de Reste à Vivre */}
                  <div className="bg-[#171118] border border-[#2d2230] rounded-2xl p-5 mb-6">
                    <h3 className="font-bold text-white mb-2 flex items-center justify-between w-full">
                      <span className="flex items-center"><TrendingUp className="w-5 h-5 mr-2 text-[#ff154d]"/> Simulateur de Reste à Vivre</span>
                      {showSimSaved && (
                        <span className="text-[11px] text-emerald-400 font-bold bg-emerald-500/10 px-2.5 py-0.5 rounded-lg border border-emerald-500/20 animate-pulse">Enregistré</span>
                      )}
                    </h3>
                    <p className="text-[13px] text-[#8e848c] mb-5 leading-relaxed">
                      Estimez le reste à vivre par personne en simulant des revenus fictifs face aux charges de la trame par défaut.
                    </p>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="text-[11px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Emmanuel (€)</label>
                        <input 
                          type="number" 
                          value={simSalaireEmmanuel} 
                          onChange={(e) => {
                            setSimSalaireEmmanuel(e.target.value);
                            localStorage.setItem('sim_salaire_emmanuel', e.target.value);
                          }} 
                          onBlur={() => saveSimSalariesToSupabase(simSalaireEmmanuel, simSalaireOceane, simAides)}
                          className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-3 py-2 text-[14px] text-white font-bold focus:outline-none focus:border-[#ff154d]" 
                          style={{ fontFamily: "'Lekton', monospace" }} 
                        />
                      </div>
                      <div>
                        <label className="text-[11px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Océane (€)</label>
                        <input 
                          type="number" 
                          value={simSalaireOceane} 
                          onChange={(e) => {
                            setSimSalaireOceane(e.target.value);
                            localStorage.setItem('sim_salaire_oceane', e.target.value);
                          }} 
                          onBlur={() => saveSimSalariesToSupabase(simSalaireEmmanuel, simSalaireOceane, simAides)}
                          className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-3 py-2 text-[14px] text-white font-bold focus:outline-none focus:border-[#ff154d]" 
                          style={{ fontFamily: "'Lekton', monospace" }} 
                        />
                      </div>
                    </div>
                    <div className="mb-5">
                      <label className="text-[11px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Aides & Autres (€)</label>
                      <input 
                        type="number" 
                        value={simAides} 
                        onChange={(e) => {
                          setSimAides(e.target.value);
                          localStorage.setItem('sim_aides', e.target.value);
                        }} 
                        onBlur={() => saveSimSalariesToSupabase(simSalaireEmmanuel, simSalaireOceane, simAides)}
                        className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-3 py-2 text-[14px] text-white font-bold focus:outline-none focus:border-[#ff154d]" 
                        style={{ fontFamily: "'Lekton', monospace" }} 
                      />
                    </div>

                    {/* Résultat de la simulation */}
                    {(() => {
                      const totalSimRevenus = (Number(simSalaireEmmanuel) || 0) + (Number(simSalaireOceane) || 0) + (Number(simAides) || 0);
                      const totalSimCharges = defaultExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
                      const simResteAVivre = totalSimRevenus - totalSimCharges;
                      const simResteChacun = simResteAVivre / 2;

                      return (
                        <div className="bg-[#211824] border border-[#3d2e40] rounded-xl p-4 space-y-3">
                          <div className="flex justify-between items-center text-[13px] border-b border-[#2d2230] pb-2">
                            <span className="text-[#8e848c]">Total revenus simulés :</span>
                            <span className="text-emerald-400 font-bold" style={{ fontFamily: "'Lekton', monospace" }}>{totalSimRevenus.toFixed(2)} €</span>
                          </div>
                          <div className="flex justify-between items-center text-[13px] border-b border-[#2d2230] pb-2">
                            <span className="text-[#8e848c]">Charges de la trame :</span>
                            <span className="text-white font-bold" style={{ fontFamily: "'Lekton', monospace" }}>-{totalSimCharges.toFixed(2)} €</span>
                          </div>
                          <div className="flex justify-between items-center text-[14px] border-b border-[#2d2230] pb-2 font-medium font-bold text-white">
                            <span>Reste à vivre global :</span>
                            <span style={{ fontFamily: "'Lekton', monospace" }}>{simResteAVivre.toFixed(2)} €</span>
                          </div>
                          <div className="pt-1 flex flex-col space-y-1">
                            <span className="text-[11px] uppercase font-bold text-[#a89ea6]">Reste à vivre par personne :</span>
                            <div className="grid grid-cols-2 gap-2 mt-1">
                              <div className="bg-[#171118] border border-[#2d2230] rounded-lg p-2.5 text-center">
                                <p className="text-[10px] text-[#8e848c] uppercase font-bold mb-0.5">Emmanuel</p>
                                <p className="text-[15px] font-bold text-emerald-400" style={{ fontFamily: "'Lekton', monospace" }}>{simResteChacun.toFixed(2)} €</p>
                              </div>
                              <div className="bg-[#171118] border border-[#2d2230] rounded-lg p-2.5 text-center">
                                <p className="text-[10px] text-[#8e848c] uppercase font-bold mb-0.5">Océane</p>
                                <p className="text-[15px] font-bold text-emerald-400" style={{ fontFamily: "'Lekton', monospace" }}>{simResteChacun.toFixed(2)} €</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </>
              )}

              {activeSettingsTab === 'sources' && (
                <div className="bg-[#171118] border border-[#2d2230] rounded-2xl p-5 mb-6">
                  <h3 className="font-bold text-white mb-2 flex items-center"><CreditCard className="w-5 h-5 mr-2 text-[#ff154d]"/> Sources de paiement</h3>
                  <p className="text-[13px] text-[#8e848c] mb-5 leading-relaxed">
                    Gérez les comptes ou moyens de paiement disponibles lors de la saisie d'une dépense ou d'une avance.
                  </p>

                  <div className="space-y-2 mb-4">
                    {paymentSources.map(source => (
                      <div key={source.id} className="flex justify-between items-center p-3 bg-[#211824] border border-[#3d2e40] rounded-xl">
                        <div className="flex flex-col">
                          <span className="text-white font-medium text-[14px]">{source.name}</span>
                          {source.owner && (
                             <span className="text-[10px] uppercase font-bold text-[#a89ea6] mt-0.5">
                               {source.owner === 'commun' ? 'Commun' : source.owner === 'emmanuel' ? 'Emmanuel' : 'Océane'}
                             </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-3">
                          <button 
                            onClick={() => {
                              setEditingPaymentSource(source);
                              setIsPaymentSourceModalOpen(true);
                            }}
                            className="text-[#a89ea6] hover:text-white transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={async () => {
                              const updatedSources = paymentSources.filter(s => s.id !== source.id);
                              setPaymentSources(updatedSources);
                              await savePaymentSourcesToSupabase(updatedSources);
                              showToast("Source de paiement supprimée !");
                            }}
                            className="text-[#a89ea6] hover:text-[#ff154d] transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button 
                    onClick={() => {
                      setEditingPaymentSource({ id: '0', name: '', owner: 'commun' });
                      setIsPaymentSourceModalOpen(true);
                    }}
                    className="w-full text-[13px] font-bold text-[#ff154d] bg-[#ff154d]/10 py-3 rounded-xl border border-[#ff154d]/30 flex items-center justify-center transition-all hover:bg-[#ff154d]/20"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Ajouter une source
                  </button>
                </div>
              )}

              {activeSettingsTab === 'categories' && (
                <div className="bg-[#171118] border border-[#2d2230] rounded-2xl p-5 mb-6">
                  <h3 className="font-bold text-white mb-2 flex items-center"><PieChart className="w-5 h-5 mr-2 text-[#ff154d]"/> Catégories de dépenses</h3>
                  <p className="text-[13px] text-[#8e848c] mb-5 leading-relaxed">
                    Gérez la liste des catégories globales utilisées pour classer vos dépenses et créer vos enveloppes mensuelles.
                  </p>

                  <div className="space-y-2 mb-4">
                    {expenseCategories.map(cat => (
                      <div key={cat.id} className="flex justify-between items-center p-3 bg-[#211824] border border-[#3d2e40] rounded-xl">
                        <span className="text-white font-medium text-[14px]">{cat.name}</span>
                        <div className="flex items-center space-x-3">
                          <button 
                            onClick={() => {
                              setEditingCategory(cat);
                              setIsCategoryModalOpen(true);
                            }}
                            className="text-[#a89ea6] hover:text-white transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => setExpenseCategories(expenseCategories.filter(c => c.id !== cat.id))}
                            className="text-[#a89ea6] hover:text-[#ff154d] transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button 
                    onClick={() => {
                      setEditingCategory({ id: '0', name: '' });
                      setIsCategoryModalOpen(true);
                    }}
                    className="w-full text-[13px] font-bold text-[#ff154d] bg-[#ff154d]/10 py-3 rounded-xl border border-[#ff154d]/30 flex items-center justify-center transition-all hover:bg-[#ff154d]/20"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Ajouter une catégorie
                  </button>
                </div>
              )}

              {activeSettingsTab === 'compte' && (
                <div className="space-y-6">
                  <div className="bg-[#171118] border border-[#2d2230] rounded-2xl p-5">
                    <h3 className="font-bold text-white mb-4 flex items-center"><User className="w-5 h-5 mr-2 text-[#a89ea6]"/> Mon Compte</h3>
                    <button className="w-full text-left py-3 border-b border-[#2d2230] text-[#f2f0f2] font-medium flex justify-between items-center">
                      <span className="truncate mr-2">{currentUser?.email || 'Profil & Sécurité'}</span> <ChevronRight className="w-4 h-4 text-[#a89ea6] shrink-0"/>
                    </button>
                    <button onClick={logout} className="w-full text-left py-3 text-[#ff154d] font-bold mt-2 cursor-pointer">
                      Déconnexion
                    </button>
                  </div>

                  <div className="bg-[#171118] border border-[#2d2230] rounded-2xl p-5 shadow-lg relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-[#ff154d]/5 rounded-full blur-xl pointer-events-none"></div>
                    
                    <h3 className="font-bold text-white mb-2 flex items-center">
                      <Bell className="w-5 h-5 mr-2.5 text-[#ff154d]"/> 
                      Notifications Système
                    </h3>
                    <p className="text-[12.5px] text-[#8e848c] mb-4 leading-relaxed">
                      Configurez les alertes et les rappels système natifs sur votre appareil.
                    </p>
                    
                    <div className="space-y-3">
                      <button 
                        onClick={async () => {
                          const success = await requestNotificationPermissions();
                          if (success) {
                            showToast("Notifications autorisées !");
                          } else {
                            alert("Veuillez autoriser les notifications dans les réglages iOS de votre iPhone.");
                          }
                        }}
                        className="w-full bg-[#211824] hover:bg-[#2d2230] border border-[#3d2e40] text-white text-[13px] font-bold py-3 rounded-xl transition-all flex items-center justify-center animate-pulse"
                      >
                        <Bell className="w-4 h-4 mr-2 text-emerald-400" /> Activer l'accès système iOS
                      </button>
                      
                      <button 
                        onClick={async () => {
                          const success = await scheduleTestNotification();
                          if (success) {
                            showToast("Notification programmée (5s) !");
                          } else {
                            alert("Impossible de programmer le test. Vérifiez les permissions dans vos réglages iOS.");
                          }
                        }}
                        className="w-full bg-[#ff154d]/10 hover:bg-[#ff154d]/20 border border-[#ff154d]/30 text-[#ff154d] text-[13px] font-bold py-3 rounded-xl transition-all flex items-center justify-center"
                      >
                        <Smartphone className="w-4 h-4 mr-2" /> Envoyer un test natif (5s)
                      </button>
                    </div>

                    {/* Choix des interactions de notification */}
                    <div className="border-t border-[#2d2230] pt-4 mt-5 space-y-3">
                      <h4 className="text-[11px] font-bold text-[#a89ea6] uppercase tracking-wider mb-2">Choisir les alertes actives</h4>
                      
                      <div className="flex justify-between items-center bg-[#211824]/40 p-3 rounded-xl border border-[#3d2e40]/30">
                        <div>
                          <p className="text-white text-[13px] font-bold">Rappels de prélèvements</p>
                          <p className="text-[11px] text-[#8e848c] mt-0.5">Alerte 1 jour avant l'échéance</p>
                        </div>
                        <button 
                          onClick={() => setNotificationPrefs({...notificationPrefs, billReminders: !notificationPrefs.billReminders})}
                          className={`w-10 h-5.5 rounded-full p-0.5 flex items-center transition-colors ${notificationPrefs.billReminders ? 'bg-emerald-500 justify-end' : 'bg-[#2d2230] justify-start'}`}
                        >
                          <div className="w-4.5 h-4.5 bg-white rounded-full shadow-sm"></div>
                        </button>
                      </div>

                      <div className="flex justify-between items-center bg-[#211824]/40 p-3 rounded-xl border border-[#3d2e40]/30">
                        <div>
                          <p className="text-white text-[13px] font-bold">Dépassement de budget</p>
                          <p className="text-[11px] text-[#8e848c] mt-0.5">Alerte si une enveloppe dépasse 90%</p>
                        </div>
                        <button 
                          onClick={() => setNotificationPrefs({...notificationPrefs, budgetAlerts: !notificationPrefs.budgetAlerts})}
                          className={`w-10 h-5.5 rounded-full p-0.5 flex items-center transition-colors ${notificationPrefs.budgetAlerts ? 'bg-emerald-500 justify-end' : 'bg-[#2d2230] justify-start'}`}
                        >
                          <div className="w-4.5 h-4.5 bg-white rounded-full shadow-sm"></div>
                        </button>
                      </div>

                      <div className="flex justify-between items-center bg-[#211824]/40 p-3 rounded-xl border border-[#3d2e40]/30">
                        <div>
                          <p className="text-white text-[13px] font-bold">Avances & Remboursements</p>
                          <p className="text-[11px] text-[#8e848c] mt-0.5">Alerte lors des mouvements de dettes</p>
                        </div>
                        <button 
                          onClick={() => setNotificationPrefs({...notificationPrefs, advancesAlerts: !notificationPrefs.advancesAlerts})}
                          className={`w-10 h-5.5 rounded-full p-0.5 flex items-center transition-colors ${notificationPrefs.advancesAlerts ? 'bg-emerald-500 justify-end' : 'bg-[#2d2230] justify-start'}`}
                        >
                          <div className="w-4.5 h-4.5 bg-white rounded-full shadow-sm"></div>
                        </button>
                      </div>

                      <div className="flex justify-between items-center bg-[#211824]/40 p-3 rounded-xl border border-[#3d2e40]/30">
                        <div>
                          <p className="text-white text-[13px] font-bold">Synthèses mensuelles</p>
                          <p className="text-[11px] text-[#8e848c] mt-0.5">Notification de clôture en fin de mois</p>
                        </div>
                        <button 
                          onClick={() => setNotificationPrefs({...notificationPrefs, monthlySummaryAlerts: !notificationPrefs.monthlySummaryAlerts})}
                          className={`w-10 h-5.5 rounded-full p-0.5 flex items-center transition-colors ${notificationPrefs.monthlySummaryAlerts ? 'bg-emerald-500 justify-end' : 'bg-[#2d2230] justify-start'}`}
                        >
                          <div className="w-4.5 h-4.5 bg-white rounded-full shadow-sm"></div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {activeSettingsTab === 'trame' && (
              <div className="border-t border-[#2d2230] bg-[#171118]/95 backdrop-blur-md p-4 sticky bottom-0 left-0 right-0 z-10 max-w-lg mx-auto w-full rounded-t-2xl shadow-[0_-8px_30px_rgba(0,0,0,0.6)]">
                <div className="flex justify-between items-center space-x-3">
                  <div className="flex-1 bg-[#211824]/60 border border-[#3d2e40] rounded-xl p-2.5">
                    <div className="flex flex-col items-center">
                      <span className="text-[10px] text-[#8e848c] uppercase font-bold mb-0.5">Reste Océane (Sim.)</span>
                      <span className={`text-[15px] font-bold ${(((Number(simSalaireEmmanuel) || 0) + (Number(simSalaireOceane) || 0) + (Number(simAides) || 0) - defaultExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0)) / 2) >= 0 ? 'text-emerald-400' : 'text-[#ff154d]'}`} style={{ fontFamily: "'Lekton', monospace" }}>
                        {(((Number(simSalaireEmmanuel) || 0) + (Number(simSalaireOceane) || 0) + (Number(simAides) || 0) - defaultExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0)) / 2).toFixed(2)} €
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 bg-[#211824]/60 border border-[#3d2e40] rounded-xl p-2.5">
                    <div className="flex flex-col items-center">
                      <span className="text-[10px] text-[#8e848c] uppercase font-bold mb-0.5">Reste Emmanuel (Sim.)</span>
                      <span className={`text-[15px] font-bold ${(((Number(simSalaireEmmanuel) || 0) + (Number(simSalaireOceane) || 0) + (Number(simAides) || 0) - defaultExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0)) / 2) >= 0 ? 'text-emerald-400' : 'text-[#ff154d]'}`} style={{ fontFamily: "'Lekton', monospace" }}>
                        {(((Number(simSalaireEmmanuel) || 0) + (Number(simSalaireOceane) || 0) + (Number(simAides) || 0) - defaultExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0)) / 2).toFixed(2)} €
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {selectedPerson && (
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }} className="fixed inset-0 z-[90] bg-[#110a10] flex flex-col" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 20px)' }}>
            <div className="flex justify-between items-center p-5 border-b border-[#2d2230] bg-[#171118]">
              <h2 className="text-[20px] font-bold text-white flex items-center">
                <User className="w-5 h-5 mr-2 text-[#a89ea6]"/> Dépenses {selectedPerson}
              </h2>
              <button onClick={() => setSelectedPerson(null)} className="p-2 bg-[#211824] rounded-full text-[#a89ea6]"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 overflow-y-auto pb-32">
              <div className="bg-[#171118] border border-[#2d2230] rounded-xl p-4 text-center mb-6 flex flex-col items-center">
                 <p className="text-[#a89ea6] text-[14px] font-bold mb-1">Reste à dépenser</p>
                 <div className="flex items-center justify-center space-x-3">
                   <h1 className="text-[36px] text-white font-bold leading-none" style={{ fontFamily: "'Lekton', monospace" }}>
                     {selectedPersonBudget ? selectedPersonBudget.remaining.toFixed(2) : calculatedResteChacun.toFixed(2)}€
                   </h1>
                 </div>
                 <div className="mt-2 text-[12px] font-bold text-[#ff154d] bg-[#ff154d]/10 px-3 py-1 rounded-full border border-[#ff154d]/20 inline-block">
                   Reste à vivre global : {calculatedResteChacun.toFixed(2)}€
                 </div>
              </div>
              <h3 className="font-bold text-white mb-3">Historique personnel</h3>
              <div className="space-y-3">
                {selectedPersonBudget?.expenses.length === 0 ? (
                  <div className="p-4 text-center text-[#a89ea6] text-[13px] bg-[#171118] rounded-xl border border-[#2d2230]">Aucune dépense trouvée.</div>
                ) : selectedPersonBudget?.expenses.map(expense => (
                  <div key={expense.id} className="bg-[#171118] rounded-xl border border-[#2d2230] p-3 flex justify-between items-center group cursor-pointer" onClick={() => { setEditingExpense(expense); setIsEditExpenseOpen(true); }}>
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 bg-[#211824] border border-[#3d2e40] rounded-lg flex items-center justify-center">
                        <ShoppingCart className="w-4 h-4 text-[#a89ea6]" />
                      </div>
                      <div>
                        <h4 className="font-bold text-[14px] text-white group-hover:text-[#ff154d] transition-colors">{expense.title}</h4>
                        <p className="text-[11px] text-[#8e848c]">{expense.due_date} • {expense.category}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="font-bold text-white mr-1" style={{ fontFamily: "'Lekton', monospace" }}>-{Math.abs(expense.amount).toFixed(2)}€</div>
                      <button className="text-[#a89ea6] hover:text-white transition-colors p-1.5 hover:bg-[#211824] rounded-lg" onClick={(e) => { e.stopPropagation(); setEditingExpense(expense); setIsEditExpenseOpen(true); }} title="Modifier">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button className="text-[#a89ea6] hover:text-[#ff154d] transition-colors p-1.5 hover:bg-[#ff154d]/10 rounded-lg" onClick={(e) => { e.stopPropagation(); handleDeleteExpense(expense.id); }} title="Supprimer">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="absolute bottom-6 left-0 right-0 px-5">
              <button 
                onClick={() => {
                  setExpenseIsBudgetized(true);
                  const actualBudget = realBudgets.find(b => b.owner === selectedPerson);
                  if (actualBudget?.id) {
                    setExpenseBudgetId(actualBudget.id);
                  } else {
                    setExpenseBudgetId('');
                  }
                  setNewExpenseForm({ title: '', amount: '', date: '', category: selectedPerson || '' });
                  setIsAddExpenseOpen(true);
                }} 
                className="w-full bg-[#ff154d] text-white font-bold py-4 rounded-xl shadow-[0_0_15px_rgba(255,21,77,0.3)]"
              >
                + Ajouter une dépense perso
              </button>
            </div>
          </motion.div>
        )}

        {isAddExpenseOpen && (
          <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm">
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25, stiffness: 300 }} className="w-full max-w-lg bg-[#171118] rounded-t-3xl border-t border-[#2d2230] p-6 pb-12 shadow-[0_-10px_40px_rgba(0,0,0,0.5)]">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-[20px] font-bold text-white">Nouvelle dépense</h2>
                <button onClick={() => setIsAddExpenseOpen(false)} className="p-2 bg-[#211824] rounded-full text-[#a89ea6]"><X className="w-5 h-5"/></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Titre</label>
                  <input type="text" value={newExpenseForm.title} onChange={e => setNewExpenseForm({...newExpenseForm, title: e.target.value})} placeholder="Ex: Courses Carrefour" className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#ff154d]" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Montant (€)</label>
                    <input type="number" value={newExpenseForm.amount} onChange={e => setNewExpenseForm({...newExpenseForm, amount: e.target.value})} placeholder="0.00" className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white font-bold text-[18px] focus:outline-none focus:border-[#ff154d]" style={{ fontFamily: "'Lekton', monospace" }} />
                  </div>
                  <div>
                    <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Jour du mois (1-31)</label>
                    <input type="number" min="1" max="31" placeholder="Ex: 15" value={newExpenseForm.date} onChange={e => setNewExpenseForm({...newExpenseForm, date: e.target.value})} className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white font-bold focus:outline-none focus:border-[#ff154d]" style={{ fontFamily: "'Lekton', monospace" }} />
                  </div>
                </div>
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Catégorie</label>
                  <select value={newExpenseForm.category} onChange={(e) => setNewExpenseForm({...newExpenseForm, category: e.target.value})} className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#ff154d] appearance-none">
                    <option value="" disabled>Sélectionner une catégorie</option>
                    {expenseCategories.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Source de paiement</label>
                  <select defaultValue="" className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#ff154d] appearance-none">
                    <option value="" disabled>Sélectionner une source</option>
                    {paymentSources.map(source => (
                      <option key={source.id} value={source.id}>
                        {source.name} {source.owner ? `(${source.owner === 'commun' ? 'Commun' : source.owner === 'emmanuel' ? 'Emmanuel' : 'Océane'})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                {!selectedPerson && (
                  <div className="flex flex-col space-y-3 bg-[#211824] border border-[#3d2e40] rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-white text-[14px]">Budgétiser</p>
                        <p className="text-[12px] text-[#8e848c]">Lier à un budget de la trame</p>
                      </div>
                      <button 
                        onClick={() => setExpenseIsBudgetized(!expenseIsBudgetized)}
                        className={`w-12 h-6 rounded-full p-1 flex items-center transition-colors ${expenseIsBudgetized ? 'bg-[#ff154d] justify-end' : 'bg-[#2d2230] justify-start'}`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full shadow-sm"></div>
                      </button>
                    </div>
                    {expenseIsBudgetized && (
                      <div className="mt-2 pt-3 border-t border-[#3d2e40]">
                        <select 
                          value={expenseBudgetId}
                          onChange={(e) => setExpenseBudgetId(e.target.value)}
                          className="w-full bg-[#171118] border border-[#3d2e40] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#ff154d] appearance-none"
                        >
                          <option value="" disabled>Sélectionner un budget</option>
                          {!activeMonth ? (
                            defaultExpenses
                              .filter(t => t.isBudgetized !== false && !t.title.toLowerCase().includes('emmanuel') && !t.title.toLowerCase().includes('océane') && !t.title.toLowerCase().includes('océanne'))
                              .map(t => (
                                <option key={t.id || t.title} value={t.title}>{t.title}</option>
                              ))
                          ) : (
                            realBudgets
                              .filter(b => b.type === 'commun')
                              .filter(b => realExpenses.some(e => 
                                (e.category === 'mensuel' || e.category?.startsWith('mensuel')) && 
                                e.is_budgetized !== false && 
                                (e.budget_id === b.id || e.title.toLowerCase() === b.name.toLowerCase())
                              ))
                              .filter((b, idx, arr) => idx === arr.findIndex(t => t.name.toLowerCase() === b.name.toLowerCase()))
                              .map(b => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                              ))
                          )}
                        </select>
                      </div>
                    )}
                  </div>
                )}
                <button onClick={handleAddExpense} className="w-full mt-4 bg-[#ff154d] hover:bg-[#ff3b68] text-white font-bold py-4 rounded-xl shadow-[0_0_15px_rgba(255,21,77,0.3)] transition-all">
                  Enregistrer
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {isEditExpenseOpen && editingExpense && (
          <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm">
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25, stiffness: 300 }} className="w-full max-w-lg bg-[#171118] rounded-t-3xl border-t border-[#2d2230] p-6 pb-12 shadow-[0_-10px_40px_rgba(0,0,0,0.5)]">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-[20px] font-bold text-white">Modifier la dépense</h2>
                <button onClick={() => setIsEditExpenseOpen(false)} className="p-2 bg-[#211824] rounded-full text-[#a89ea6]"><X className="w-5 h-5"/></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Titre</label>
                  <input 
                    type="text" 
                    value={editingExpense.title || ''} 
                    onChange={e => setEditingExpense({...editingExpense, title: e.target.value})} 
                    placeholder="Ex: Courses Carrefour" 
                    className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#ff154d]" 
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Montant (€)</label>
                    <input 
                      type="number" 
                      value={Math.abs(editingExpense.amount) || ''} 
                      onChange={e => setEditingExpense({...editingExpense, amount: Number(e.target.value)})} 
                      placeholder="0.00" 
                      className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white font-bold text-[18px] focus:outline-none focus:border-[#ff154d]" 
                      style={{ fontFamily: "'Lekton', monospace" }} 
                    />
                  </div>
                  <div>
                    <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Jour du mois (1-31)</label>
                    <input 
                      type="number" 
                      min="1" max="31" 
                      value={editingExpense.due_date ? (() => {
                        const d = new Date(editingExpense.due_date);
                        return isNaN(d.getTime()) ? editingExpense.due_date : d.getDate().toString();
                      })() : ''} 
                      onChange={e => {
                        const year = currentDate.getFullYear();
                        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                        const day = String(e.target.value || '01').padStart(2, '0');
                        setEditingExpense({...editingExpense, due_date: `${year}-${month}-${day}`});
                      }} 
                      placeholder="Ex: 15" 
                      className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white font-bold focus:outline-none focus:border-[#ff154d]" 
                      style={{ fontFamily: "'Lekton', monospace" }} 
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Catégorie</label>
                  <select 
                    value={editingExpense.category || ''} 
                    onChange={e => setEditingExpense({...editingExpense, category: e.target.value})} 
                    className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#ff154d] appearance-none"
                  >
                    <option value="" disabled>Sélectionner une catégorie</option>
                    {expenseCategories.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Source de paiement</label>
                  <select defaultValue="" className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#ff154d] appearance-none">
                    <option value="" disabled>Sélectionner une source</option>
                    {paymentSources.map(source => (
                      <option key={source.id} value={source.id}>
                        {source.name} {source.owner ? `(${source.owner === 'commun' ? 'Commun' : source.owner === 'emmanuel' ? 'Emmanuel' : 'Océane'})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                {!selectedPerson && (
                  <div className="flex flex-col space-y-3 bg-[#211824] border border-[#3d2e40] rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-white text-[14px]">Budgétiser</p>
                        <p className="text-[12px] text-[#8e848c]">Lier à un budget de la trame</p>
                      </div>
                      <button 
                        onClick={() => setExpenseIsBudgetized(!expenseIsBudgetized)}
                        className={`w-12 h-6 rounded-full p-1 flex items-center transition-colors ${expenseIsBudgetized ? 'bg-[#ff154d] justify-end' : 'bg-[#2d2230] justify-start'}`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full shadow-sm"></div>
                      </button>
                    </div>
                    {expenseIsBudgetized && (
                      <div className="mt-2 pt-3 border-t border-[#3d2e40]">
                        <select 
                          value={expenseBudgetId}
                          onChange={(e) => setExpenseBudgetId(e.target.value)}
                          className="w-full bg-[#171118] border border-[#3d2e40] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#ff154d] appearance-none"
                        >
                          <option value="" disabled>Sélectionner un budget</option>
                          {!activeMonth ? (
                            defaultExpenses
                              .filter(t => t.isBudgetized !== false && !t.title.toLowerCase().includes('emmanuel') && !t.title.toLowerCase().includes('océane') && !t.title.toLowerCase().includes('océanne'))
                              .map(t => (
                                <option key={t.id || t.title} value={t.title}>{t.title}</option>
                              ))
                          ) : (
                            realBudgets
                              .filter(b => b.type === 'commun')
                              .filter(b => realExpenses.some(e => 
                                (e.category === 'mensuel' || e.category?.startsWith('mensuel')) && 
                                e.is_budgetized !== false && 
                                (e.budget_id === b.id || e.title.toLowerCase() === b.name.toLowerCase())
                              ))
                              .filter((b, idx, arr) => idx === arr.findIndex(t => t.name.toLowerCase() === b.name.toLowerCase()))
                              .map(b => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                              ))
                          )}
                        </select>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex space-x-3 mt-4">
                  <button 
                    type="button"
                    onClick={async () => {
                      if (editingExpense) {
                        const idToDelete = editingExpense.id;
                        setIsEditExpenseOpen(false);
                        setEditingExpense(null);
                        await handleDeleteExpense(idToDelete);
                      }
                    }} 
                    className="flex-1 bg-[#ff154d]/10 hover:bg-[#ff154d]/20 text-[#ff154d] font-bold py-4 rounded-xl border border-[#ff154d]/30 transition-all flex items-center justify-center"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                  </button>
                  <button onClick={handleUpdateExpense} className="flex-[2] bg-[#ff154d] hover:bg-[#ff3b68] text-white font-bold py-4 rounded-xl shadow-[0_0_15px_rgba(255,21,77,0.3)] transition-all">
                    Mettre à jour
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {isAddAdvanceOpen && (
          <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm">
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25, stiffness: 300 }} className="w-full max-w-lg bg-[#171118] rounded-t-3xl border-t border-[#2d2230] p-6 pb-12 shadow-[0_-10px_40px_rgba(0,0,0,0.5)]">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-[20px] font-bold text-white">{editingAdvance ? 'Modifier l\'avance' : 'Ajouter une avance'}</h2>
                <button onClick={() => { setIsAddAdvanceOpen(false); setEditingAdvance(null); }} className="p-2 bg-[#211824] rounded-full text-[#a89ea6]"><X className="w-5 h-5"/></button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2 p-1 bg-[#211824] border border-[#3d2e40] rounded-xl">
                  <button onClick={() => setNewAdvanceForm({...newAdvanceForm, payer: 'Emmanuel'})} className={`${newAdvanceForm.payer === 'Emmanuel' ? 'bg-[#171118] text-white shadow-sm' : 'text-[#a89ea6] hover:text-white transition-colors'} py-2 rounded-lg font-bold text-[13px]`}>Emmanuel a payé</button>
                  <button onClick={() => setNewAdvanceForm({...newAdvanceForm, payer: 'Océane'})} className={`${newAdvanceForm.payer === 'Océane' ? 'bg-[#171118] text-white shadow-sm' : 'text-[#a89ea6] hover:text-white transition-colors'} py-2 rounded-lg font-bold text-[13px]`}>Océane a payé</button>
                </div>
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-2 block">Pour qui ?</label>
                  <div className="grid grid-cols-3 gap-2 p-1 bg-[#211824] border border-[#3d2e40] rounded-xl">
                    <button onClick={() => setNewAdvanceForm({...newAdvanceForm, forWhom: 'Commun'})} className={`${newAdvanceForm.forWhom === 'Commun' ? 'bg-[#171118] text-white shadow-sm' : 'text-[#a89ea6] hover:text-white transition-colors'} py-2 rounded-lg font-bold text-[12px]`}>Commun (50/50)</button>
                    <button onClick={() => setNewAdvanceForm({...newAdvanceForm, forWhom: 'Emmanuel'})} className={`${newAdvanceForm.forWhom === 'Emmanuel' ? 'bg-[#171118] text-white shadow-sm' : 'text-[#a89ea6] hover:text-white transition-colors'} py-2 rounded-lg font-bold text-[12px]`}>Emmanuel</button>
                    <button onClick={() => setNewAdvanceForm({...newAdvanceForm, forWhom: 'Océane'})} className={`${newAdvanceForm.forWhom === 'Océane' ? 'bg-[#171118] text-white shadow-sm' : 'text-[#a89ea6] hover:text-white transition-colors'} py-2 rounded-lg font-bold text-[12px]`}>Océane</button>
                  </div>
                </div>
                {(() => {
                  let debtor = '';
                  let share = '';
                  if (newAdvanceForm.forWhom === 'Commun') {
                    debtor = newAdvanceForm.payer === 'Emmanuel' ? 'Océane' : 'Emmanuel';
                    share = '50% (Commun)';
                  } else if (newAdvanceForm.forWhom !== newAdvanceForm.payer) {
                    debtor = newAdvanceForm.forWhom;
                    share = '100%';
                  } else {
                    debtor = 'Personne (Ajustement personnel)';
                    share = '0%';
                  }
                  return (
                    <div className="bg-[#211824] border border-[#3d2e40] p-3 rounded-xl flex items-center justify-between text-[13px]">
                      <span className="text-[#a89ea6] font-medium">Qui rembourse ?</span>
                      <span className="text-[#ff154d] font-bold">{debtor} ({share})</span>
                    </div>
                  );
                })()}
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Motif</label>
                  <input type="text" value={newAdvanceForm.reason} onChange={(e) => setNewAdvanceForm({...newAdvanceForm, reason: e.target.value})} placeholder="Ex: Cadeau commun" className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#ff154d]" />
                </div>
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Source de paiement</label>
                  <select className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#ff154d] appearance-none">
                    <option value="" disabled selected>Sélectionner une source</option>
                    {paymentSources.map(source => (
                      <option key={source.id} value={source.id}>
                        {source.name} {source.owner ? `(${source.owner === 'commun' ? 'Commun' : source.owner === 'emmanuel' ? 'Emmanuel' : 'Océane'})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Montant total payé (€)</label>
                  <input type="number" value={newAdvanceForm.amount} onChange={(e) => setNewAdvanceForm({...newAdvanceForm, amount: e.target.value})} placeholder="0.00" className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white font-bold text-[18px] focus:outline-none focus:border-[#ff154d]" style={{ fontFamily: "'Lekton', monospace" }} />
                </div>
                <button onClick={handleAddAdvance} className="w-full mt-4 bg-white hover:bg-gray-200 text-[#110a10] font-bold py-4 rounded-xl shadow-[0_0_15px_rgba(255,255,255,0.2)] transition-all">
                  {editingAdvance ? 'Sauvegarder les modifications' : 'Valider l\'avance'}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {repayingAdvance && (
          <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm">
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25, stiffness: 300 }} className="w-full max-w-lg bg-[#171118] rounded-t-3xl border-t border-[#2d2230] p-6 pb-12 shadow-[0_-10px_40px_rgba(0,0,0,0.5)]">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-[20px] font-bold text-white">Rembourser : {repayingAdvance.reason}</h2>
                <button onClick={() => setRepayingAdvance(null)} className="p-2 bg-[#211824] rounded-full text-[#a89ea6]"><X className="w-5 h-5"/></button>
              </div>
              <div className="space-y-4">
                <div className="p-3 bg-[#211824] border border-[#3d2e40] rounded-xl flex justify-between items-center">
                  <span className="text-[13px] text-[#a89ea6]">Reste à payer</span>
                  <span className="font-bold text-[16px] text-[#ff154d]" style={{ fontFamily: "'Lekton', monospace" }}>
                    {(repayingAdvance.amount - (repayingAdvance.repayments?.reduce((s:number,r:any)=>s+r.amount,0)||0)).toFixed(2)} €
                  </span>
                </div>
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Montant du remboursement (€)</label>
                  <input type="number" value={repaymentAmount} onChange={(e) => setRepaymentAmount(e.target.value)} placeholder="0.00" className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white font-bold text-[18px] focus:outline-none focus:border-[#ff154d]" style={{ fontFamily: "'Lekton', monospace" }} />
                </div>
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Date du remboursement</label>
                  <input type="date" defaultValue={new Date().toISOString().split('T')[0]} className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white font-bold text-[15px] focus:outline-none focus:border-[#ff154d]" />
                </div>
                <button onClick={handleRepayAdvance} className="w-full mt-4 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 rounded-xl shadow-[0_0_15px_rgba(16,185,129,0.3)] transition-all">
                  Valider le remboursement
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {selectedAdvance && (
          <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm">
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25, stiffness: 300 }} className="w-full max-w-lg bg-[#171118] rounded-t-3xl border-t border-[#2d2230] p-6 pb-12 shadow-[0_-10px_40px_rgba(0,0,0,0.5)] max-h-[85vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-[20px] font-bold text-white">Détails de l'avance</h2>
                  <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-lg border ${selectedAdvance.settled ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' : 'text-amber-400 bg-amber-500/10 border-amber-500/20'}`}>
                    {selectedAdvance.settled ? 'Réglée' : 'En cours'}
                  </span>
                </div>
                <button onClick={() => setSelectedAdvance(null)} className="p-2 bg-[#211824] rounded-full text-[#a89ea6]"><X className="w-5 h-5"/></button>
              </div>

              <div className="space-y-5">
                <div className="bg-[#211824] border border-[#3d2e40] rounded-xl p-4 space-y-3">
                  <div className="flex justify-between items-center text-[14px]">
                    <span className="text-[#a89ea6]">Motif :</span>
                    <span className="text-white font-bold">{selectedAdvance.reason}</span>
                  </div>
                  <div className="flex justify-between items-center text-[14px]">
                    <span className="text-[#a89ea6]">Montant payé :</span>
                    <span className="text-white font-bold" style={{ fontFamily: "'Lekton', monospace" }}>{selectedAdvance.amount.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between items-center text-[14px]">
                    <span className="text-[#a89ea6]">Payé par :</span>
                    <span className="text-white font-bold bg-[#171118] px-2.5 py-0.5 rounded border border-[#2d2230]">{selectedAdvance.payer}</span>
                  </div>
                  <div className="flex justify-between items-center text-[14px]">
                    <span className="text-[#a89ea6]">Pour qui :</span>
                    <span className="text-white font-bold bg-[#171118] px-2.5 py-0.5 rounded border border-[#2d2230]">{selectedAdvance.for_whom}</span>
                  </div>
                </div>

                <div className="bg-[#211824] border border-[#3d2e40] rounded-xl p-4">
                  <h4 className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-3">Remboursement & Répartition</h4>
                  {(() => {
                    const totalRepaid = selectedAdvance.repayments?.reduce((s: number, r: any) => s + r.amount, 0) || 0;
                    
                    // Logic to see who owes who
                    let debtor = '';
                    let creditor = selectedAdvance.payer;
                    let owesAmount = 0;
                    
                    if (selectedAdvance.for_whom === 'Commun') {
                      debtor = selectedAdvance.payer === 'Emmanuel' ? 'Océane' : 'Emmanuel';
                      owesAmount = selectedAdvance.amount / 2;
                    } else {
                      debtor = selectedAdvance.for_whom;
                      owesAmount = selectedAdvance.amount;
                    }
                    
                    const remainsToPay = Math.max(0, owesAmount - totalRepaid);

                    return (
                      <div className="space-y-3">
                        <div className="flex justify-between text-[13px]">
                          <span className="text-[#8e848c]">Qui rembourse ?</span>
                          <span className="text-white font-bold">{debtor} (doit rembourser {creditor})</span>
                        </div>
                        <div className="flex justify-between text-[13px]">
                          <span className="text-[#8e848c]">Montant dû :</span>
                          <span className="text-white font-bold" style={{ fontFamily: "'Lekton', monospace" }}>{owesAmount.toFixed(2)} €</span>
                        </div>
                        <div className="flex justify-between text-[13px]">
                          <span className="text-[#8e848c]">Déjà remboursé :</span>
                          <span className="text-emerald-400 font-bold" style={{ fontFamily: "'Lekton', monospace" }}>{totalRepaid.toFixed(2)} €</span>
                        </div>
                        <div className="flex justify-between text-[13px] border-t border-[#3d2e40] pt-2">
                          <span className="text-[#a89ea6] font-bold">Reste à payer :</span>
                          <span className="text-[#ff154d] font-bold" style={{ fontFamily: "'Lekton', monospace" }}>{remainsToPay.toFixed(2)} €</span>
                        </div>
                        
                        {owesAmount > 0 && (
                          <div className="pt-2">
                            <div className="w-full bg-[#171118] rounded-full h-2 border border-[#2d2230]">
                              <div className="bg-[#ff154d] h-2 rounded-full transition-all duration-500" style={{ width: `${Math.min(100, (totalRepaid / owesAmount) * 100)}%` }}></div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                <div className="bg-[#211824] border border-[#3d2e40] rounded-xl p-4">
                  <h4 className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-3">Historique des remboursements</h4>
                  {(!selectedAdvance.repayments || selectedAdvance.repayments.length === 0) ? (
                    <p className="text-[13px] text-[#8e848c] text-center py-2">Aucun remboursement enregistré.</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedAdvance.repayments.map((rep: any) => (
                        <div key={rep.id} className="flex justify-between items-center text-[13px] bg-[#171118] border border-[#2d2230] p-2.5 rounded-lg">
                          <span className="text-[#a89ea6]">{new Date(rep.created_at || Date.now()).toLocaleDateString('fr-FR')}</span>
                          <span className="text-emerald-400 font-bold" style={{ fontFamily: "'Lekton', monospace" }}>+{rep.amount.toFixed(2)} €</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2 pt-2">
                  <button 
                    onClick={() => {
                      setEditingAdvance(selectedAdvance);
                      setNewAdvanceForm({
                        payer: selectedAdvance.payer,
                        forWhom: selectedAdvance.for_whom,
                        reason: selectedAdvance.reason,
                        amount: selectedAdvance.amount.toString()
                      });
                      setSelectedAdvance(null);
                      setIsAddAdvanceOpen(true);
                    }}
                    className="bg-[#211824] border border-[#3d2e40] text-white py-3 rounded-xl font-bold text-[13px] hover:bg-[#2d2230]"
                  >
                    Modifier
                  </button>
                  <button 
                    onClick={async () => {
                      const idToDelete = selectedAdvance.id;
                      setSelectedAdvance(null);
                      await handleDeleteAdvance(idToDelete);
                    }}
                    className="bg-[#ff154d]/10 border border-[#ff154d]/30 text-[#ff154d] py-3 rounded-xl font-bold text-[13px] hover:bg-[#ff154d]/20"
                  >
                    Supprimer
                  </button>
                  {!selectedAdvance.settled ? (
                    <button 
                      onClick={() => {
                        setRepayingAdvance(selectedAdvance);
                        const totalRepaid = selectedAdvance.repayments?.reduce((s: number, r: any) => s + r.amount, 0) || 0;
                        const remaining = selectedAdvance.amount - totalRepaid;
                        
                        let owesAmount = selectedAdvance.for_whom === 'Commun' ? selectedAdvance.amount / 2 : selectedAdvance.amount;
                        const remainsToPay = Math.max(0, owesAmount - totalRepaid);
                        setRepaymentAmount(remainsToPay.toString());
                        setSelectedAdvance(null);
                      }}
                      className="bg-emerald-500 text-white py-3 rounded-xl font-bold text-[13px] hover:bg-emerald-600 shadow-[0_0_15px_rgba(16,185,129,0.2)]"
                    >
                      Rembourser
                    </button>
                  ) : (
                    <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 py-3 rounded-xl font-bold text-[13px] flex items-center justify-center">
                      Réglée
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {isResourcesOpen && (
          <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm">
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25, stiffness: 300 }} className="w-full max-w-lg bg-[#171118] rounded-t-3xl border-t border-[#2d2230] p-6 pb-12 shadow-[0_-10px_40px_rgba(0,0,0,0.5)]">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-[20px] font-bold text-white">
                  Ressources de {new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(currentDate)}
                </h2>
                <button onClick={() => setIsResourcesOpen(false)} className="p-2 bg-[#211824] rounded-full text-[#a89ea6]"><X className="w-5 h-5"/></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Salaire Emmanuel (€)</label>
                  <input type="number" value={salaireEmmanuel} onChange={(e) => setSalaireEmmanuel(e.target.value)} className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white font-bold text-[18px] focus:outline-none focus:border-[#ff154d]" style={{ fontFamily: "'Lekton', monospace" }} />
                </div>
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Salaire Océane (€)</label>
                  <input type="number" value={salaireOceane} onChange={(e) => setSalaireOceane(e.target.value)} className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white font-bold text-[18px] focus:outline-none focus:border-[#ff154d]" style={{ fontFamily: "'Lekton', monospace" }} />
                </div>
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Aides & Autres revenus (€)</label>
                  <input type="number" value={aides} onChange={(e) => setAides(e.target.value)} className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white font-bold text-[18px] focus:outline-none focus:border-[#ff154d]" style={{ fontFamily: "'Lekton', monospace" }} />
                </div>
                <div className="flex justify-between items-center pt-4 border-t border-[#2d2230] mt-2">
                  <p className="text-[15px] font-bold text-white">Total des revenus</p>
                  <motion.p 
                    key={(Number(salaireEmmanuel) || 0) + (Number(salaireOceane) || 0) + (Number(aides) || 0)}
                    initial={{ scale: 0.85, opacity: 0.5 }}
                    animate={{ scale: [1.1, 1], opacity: 1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 15 }}
                    className="text-[24px] font-bold text-emerald-400" 
                    style={{ fontFamily: "'Lekton', monospace" }}
                  >
                    {((Number(salaireEmmanuel) || 0) + (Number(salaireOceane) || 0) + (Number(aides) || 0)).toFixed(2)}€
                  </motion.p>
                </div>
                <button onClick={handleSaveResources} className="w-full mt-4 bg-[#ff154d] hover:bg-[#ff3b68] text-white font-bold py-4 rounded-xl shadow-[0_0_15px_rgba(255,21,77,0.3)] transition-all">
                  Enregistrer les ressources
                </button>
              </div>
            </motion.div>
          </div>
        )}
        {isDefaultExpenseModalOpen && editingDefaultExpense && (
          <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm">
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25, stiffness: 300 }} className="w-full max-w-lg bg-[#171118] rounded-t-3xl border-t border-[#2d2230] p-6 pb-12 shadow-[0_-10px_40px_rgba(0,0,0,0.5)]">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-[20px] font-bold text-white">
                  {editingDefaultExpense.id === 0 ? 'Nouvelle dépense par défaut' : 'Modifier la dépense'}
                </h2>
                <button onClick={() => setIsDefaultExpenseModalOpen(false)} className="p-2 bg-[#211824] rounded-full text-[#a89ea6]"><X className="w-5 h-5"/></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Titre</label>
                  <input 
                    type="text" 
                    placeholder="Ex: Loyer" 
                    value={editingDefaultExpense.title}
                    onChange={(e) => setEditingDefaultExpense({...editingDefaultExpense, title: e.target.value})}
                    className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#ff154d]" 
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Montant (€)</label>
                    <input 
                      type="number" 
                      placeholder="0.00" 
                      value={editingDefaultExpense.amount}
                      onChange={(e) => setEditingDefaultExpense({...editingDefaultExpense, amount: e.target.value})}
                      className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white font-bold text-[18px] focus:outline-none focus:border-[#ff154d]" 
                      style={{ fontFamily: "'Lekton', monospace" }} 
                    />
                  </div>
                  <div>
                    <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Jour de prélèvement</label>
                    <input 
                      type="number" 
                      placeholder="Ex: 5" 
                      min="1" max="31"
                      value={editingDefaultExpense.dueDate || ''}
                      onChange={(e) => setEditingDefaultExpense({...editingDefaultExpense, dueDate: e.target.value})}
                      className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white font-bold text-[18px] focus:outline-none focus:border-[#ff154d]" 
                      style={{ fontFamily: "'Lekton', monospace" }} 
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between bg-[#211824] border border-[#3d2e40] rounded-xl p-4">
                  <div>
                    <p className="font-bold text-white text-[14px]">Statut</p>
                    <p className="text-[12px] text-[#8e848c]">Prélèvement passé ?</p>
                  </div>
                  <button 
                    onClick={() => setEditingDefaultExpense({...editingDefaultExpense, isPaid: !editingDefaultExpense.isPaid})}
                    className={`w-12 h-6 rounded-full p-1 flex items-center transition-colors ${editingDefaultExpense.isPaid ? 'bg-emerald-500 justify-end' : 'bg-[#2d2230] justify-start'}`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full shadow-sm"></div>
                  </button>
                </div>
                <div className="flex items-center justify-between bg-[#211824] border border-[#3d2e40] rounded-xl p-4">
                  <div>
                    <p className="font-bold text-white text-[14px]">Budgétisé</p>
                    <p className="text-[12px] text-[#8e848c]">Afficher la jauge dans la page budget ?</p>
                  </div>
                  <button 
                    onClick={() => setEditingDefaultExpense({...editingDefaultExpense, isBudgetized: editingDefaultExpense.isBudgetized === false ? true : false})}
                    className={`w-12 h-6 rounded-full p-1 flex items-center transition-colors ${editingDefaultExpense.isBudgetized !== false ? 'bg-[#ff154d] justify-end' : 'bg-[#2d2230] justify-start'}`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full shadow-sm"></div>
                  </button>
                </div>
                <button 
                  onClick={handleSaveDefaultExpense}
                  className="w-full mt-4 bg-[#ff154d] hover:bg-[#ff3b68] text-white font-bold py-4 rounded-xl shadow-[0_0_15px_rgba(255,21,77,0.3)] transition-all"
                >
                  Sauvegarder
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {isPaymentSourceModalOpen && editingPaymentSource && (
          <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm">
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25, stiffness: 300 }} className="w-full max-w-lg bg-[#171118] rounded-t-3xl border-t border-[#2d2230] p-6 pb-12 shadow-[0_-10px_40px_rgba(0,0,0,0.5)]">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-[20px] font-bold text-white">
                  {editingPaymentSource.id === '0' ? 'Nouvelle source de paiement' : 'Modifier la source'}
                </h2>
                <button onClick={() => setIsPaymentSourceModalOpen(false)} className="p-2 bg-[#211824] rounded-full text-[#a89ea6]"><X className="w-5 h-5"/></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Nom de la source</label>
                  <input 
                    type="text" 
                    placeholder="Ex: Hello Bank" 
                    value={editingPaymentSource.name}
                    onChange={(e) => setEditingPaymentSource({...editingPaymentSource, name: e.target.value})}
                    className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#ff154d]" 
                  />
                </div>
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Propriétaire</label>
                  <select 
                    value={editingPaymentSource.owner || 'commun'}
                    onChange={(e) => setEditingPaymentSource({...editingPaymentSource, owner: e.target.value as 'commun' | 'emmanuel' | 'oceanne'})}
                    className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#ff154d] appearance-none"
                  >
                    <option value="commun">Commun</option>
                    <option value="emmanuel">Emmanuel</option>
                    <option value="oceanne">Océane</option>
                  </select>
                </div>
                <button 
                  onClick={handleSavePaymentSource}
                  className="w-full mt-4 bg-[#ff154d] hover:bg-[#ff3b68] text-white font-bold py-4 rounded-xl shadow-[0_0_15px_rgba(255,21,77,0.3)] transition-all"
                >
                  Sauvegarder
                </button>
              </div>
            </motion.div>
          </div>
        )}
        
        {/* Toast Notification */}
        {toast && (
          <motion.div 
            initial={{ y: 100, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            exit={{ y: 100, opacity: 0 }} 
            className="fixed bottom-24 left-4 right-4 z-[110] bg-[#171118] border border-[#2d2230] rounded-xl p-4 flex items-center justify-between shadow-[0_10px_40px_rgba(0,0,0,0.8)]"
          >
            <div className="flex items-center text-emerald-400 font-medium text-[14px]">
              <CheckCircle2 className="w-5 h-5 mr-3" />
              {toast.message}
            </div>
            {toast.action && (
              <button 
                onClick={() => { toast.action?.onClick(); setToast(null); }}
                className="text-[#ff154d] font-bold text-[13px] uppercase tracking-wider px-3 py-1 bg-[#ff154d]/10 rounded-lg border border-[#ff154d]/30"
              >
                {toast.action.label}
              </button>
            )}
          </motion.div>
        )}
        {isCategoryModalOpen && editingCategory && (
          <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm">
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25, stiffness: 300 }} className="w-full max-w-lg bg-[#171118] rounded-t-3xl border-t border-[#2d2230] p-6 pb-12 shadow-[0_-10px_40px_rgba(0,0,0,0.5)]">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-[20px] font-bold text-white">
                  {editingCategory.id === '0' ? 'Nouvelle catégorie' : 'Modifier la catégorie'}
                </h2>
                <button onClick={() => setIsCategoryModalOpen(false)} className="p-2 bg-[#211824] rounded-full text-[#a89ea6]"><X className="w-5 h-5"/></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-[12px] font-bold text-[#a89ea6] uppercase tracking-wider mb-1 block">Nom de la catégorie</label>
                  <input 
                    type="text" 
                    placeholder="Ex: Santé, Voiture..." 
                    value={editingCategory.name}
                    onChange={(e) => setEditingCategory({...editingCategory, name: e.target.value})}
                    className="w-full bg-[#211824] border border-[#3d2e40] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#ff154d]" 
                  />
                </div>
                <button 
                  onClick={() => {
                    if (editingCategory.id === '0') {
                      setExpenseCategories([...expenseCategories, { ...editingCategory, id: Date.now().toString() }]);
                    } else {
                      setExpenseCategories(expenseCategories.map(c => c.id === editingCategory.id ? editingCategory : c));
                    }
                    setIsCategoryModalOpen(false);
                  }} 
                  className="w-full mt-4 bg-[#ff154d] hover:bg-[#ff3b68] text-white font-bold py-4 rounded-xl shadow-[0_0_15px_rgba(255,21,77,0.3)] transition-all"
                >
                  Sauvegarder
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SupabasePrototype;
